import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings, LogOut, PackageSearch, KeyRound, X, UploadCloud, FileText, CheckCircle, AlertTriangle, AlertCircle, RefreshCw, Box, History, ChevronRight, ChevronLeft, ChevronDown, PlusCircle, Scale, Beaker, FileSpreadsheet, BarChart3, Layout, Calculator, Info, Flame, Clock, Timer, Image, ZoomIn, ZoomOut, Zap, Trash2, Scan, ExternalLink, Layers } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea, ReferenceLine, AreaChart, Area } from 'recharts';
// Loaded dynamically

import Tesseract from 'tesseract.js';
import './Dashboard.css';

const isPdfFile = (file, url) => {
    if (file) {
        if (file.type === 'application/pdf') return true;
        if (file.name && file.name.toLowerCase().endsWith('.pdf')) return true;
    }
    if (typeof url === 'string') {
        const clean = url.split('?')[0].toLowerCase();
        if (clean.endsWith('.pdf') || clean.includes('.pdf')) return true;
        if (url.startsWith('data:application/pdf') || url.includes('application/pdf')) return true;
    }
    return false;
};

const isCopperElement = (elem) => {
    const str = (elem || '').trim().toUpperCase();
    return str.includes('COPPER') || str.includes('(CU)') || str === 'CU';
};

const Dashboard = () => {
    const navigate = useNavigate();
    const userModule = localStorage.getItem('userModule') || 'CU';
    const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
    const isQualityManager = userRole === 'quality manager' || userRole === 'quality_manager' || userRole === 'qm' || userRole.includes('quality');
    let accessibleModules = [];
    try {
        const stored = localStorage.getItem('accessibleModules');
        accessibleModules = stored ? JSON.parse(stored) : [];
    } catch (e) {
        accessibleModules = [];
    }
    const [showSecretModal, setShowSecretModal] = useState(false);
    const [secretCode, setSecretCode] = useState('');
    const [error, setError] = useState('');
    const [showEmptyItemsModal, setShowEmptyItemsModal] = useState(false);

    // Metallurgy Workflow State
    const [transactions, setTransactions] = useState([]);
    const [items, setItems] = useState([]);
    const [elementSettings, setElementSettings] = useState([]);



    // Lifecycle Bootstrap
    useEffect(() => {
        if (userModule === 'FE') {
            document.body.classList.add('theme-fe');
        } else {
            document.body.classList.remove('theme-fe');
        }
        return () => {
            document.body.classList.remove('theme-fe');
        };
    }, [userModule]);

    useEffect(() => {
        const userId = localStorage.getItem('userId') || localStorage.getItem('currentUser') || 'admin';
        const headers = { 'x-module': userModule, 'x-user-id': userId };

        fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/items?module=${userModule}&userId=${encodeURIComponent(userId)}`, { headers })
            .then(res => res.json())
            .then(data => {
                if (!data || data.length === 0) {
                    if (isQualityManager) {
                        setShowEmptyItemsModal(true);
                    }
                }
                setItems(data.map(i => ({ ...i, id: i._id })));
            })
            .catch(err => console.error("Error items:", err));

        fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/batches?module=${userModule}&userId=${encodeURIComponent(userId)}`, { headers })
            .then(res => res.json())
            .then(data => {
                // Ensure the items match nicely
                const mapped = data.map(t => ({
                    ...t,
                    itemName: t.itemSpecId?.name || t.itemName || 'Unknown Reference'
                }));
                const rev = mapped.reverse();
                setTransactions(rev);
                const role = (localStorage.getItem('userRole') || '').toLowerCase();
                if ((role === 'shop floor operator' || role === 'operator') && rev.length > 0) {
                    setIsCreatingNew(false);
                    setActiveTransactionId(rev[0].id);
                }
            })
            .catch(err => console.error("Error batches:", err));

        fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/element-settings?module=${userModule}&userId=${encodeURIComponent(userId)}`, { headers })
            .then(res => res.json())
            .then(data => setElementSettings(data))
            .catch(err => console.error("Error element-settings:", err));
    }, []);
    const [activeTransactionId, setActiveTransactionId] = useState(null);
    const [isCreatingNew, setIsCreatingNew] = useState(true);
    const [viewMode, setViewMode] = useState('reports'); // 'reports' or 'charts'
    const [collapsedRounds, setCollapsedRounds] = useState({}); // roundId -> true means collapsed

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const transactionsPerPage = 5;

    const toggleRoundCollapse = (roundId) => {
        setCollapsedRounds(prev => ({ ...prev, [roundId]: !prev[roundId] }));
    };

    // Form inputs for a new round/transaction
    const [selectedItem, setSelectedItem] = useState('');
    const [baseWeight, setBaseWeight] = useState('');
    const [maxChargeWeightLbs, setMaxChargeWeightLbs] = useState('');
    const [maxChargeWeightPercent, setMaxChargeWeightPercent] = useState('');
    const [currentInputs, setCurrentInputs] = useState({});
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    // Customer filter state
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
    const [itemSearch, setItemSearch] = useState('');
    const [itemDropdownOpen, setItemDropdownOpen] = useState(false);

    // Close Batch modal state
    const [showCloseBatchModal, setShowCloseBatchModal] = useState(false);
    const [closureRemarks, setClosureRemarks] = useState('');
    const [isClosing, setIsClosing] = useState(false);

    const handleCloseBatch = async () => {
        if (!activeTransactionId) return;
        setIsClosing(true);
        const currentUser = localStorage.getItem('currentUser') || 'admin';
        const nowStr = new Date().toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/batches/${activeTransactionId}/close`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ closedBy: currentUser, closedAt: nowStr, closureRemarks })
            });
            const updated = await res.json();
            setTransactions(prev => prev.map(t =>
                t.id === activeTransactionId ? { ...updated, itemName: t.itemName } : t
            ));
            setShowCloseBatchModal(false);
            setClosureRemarks('');
        } catch (err) {
            console.error('Close batch error:', err);
        } finally {
            setIsClosing(false);
        }
    };

    // Dual Report & OCR Modal state
    const [ocrModal, setOcrModal] = useState(null); // null | { step: 'preview'|'scanning'|'review', file, previewUrl, targetSpecId, reportType: 'spectrometer'|'xray', scannedInputs }
    const [activeOcrTooltip, setActiveOcrTooltip] = useState(null);
    const [switchConfirmModal, setSwitchConfirmModal] = useState(null);
    const [spectrometerFile, setSpectrometerFile] = useState(null);
    const [spectrometerUrl, setSpectrometerUrl] = useState(null);
    const [isSpectrometerScanned, setIsSpectrometerScanned] = useState(false);
    const [xrayFile, setXrayFile] = useState(null);
    const [xrayUrl, setXrayUrl] = useState(null);
    const [isXrayScanned, setIsXrayScanned] = useState(false);

    // Upload Report Mode: 'both' | 'spectrometer' | 'xray'
    const [uploadReportMode, setUploadReportMode] = useState('both');
    const [bothUploadSubtype, setBothUploadSubtype] = useState('combined'); // 'combined' | 'separate'
    const [roundUploadReportMode, setRoundUploadReportMode] = useState('both');
    const [roundBothUploadSubtype, setRoundBothUploadSubtype] = useState('combined');

    const isCombinedFile = !!(spectrometerFile && xrayFile && spectrometerFile === xrayFile);

    const handleSwitchReportMode = (mode, isRound = false) => {
        if (isRound) {
            setRoundUploadReportMode(mode);
        } else {
            setUploadReportMode(mode);
        }

        if (mode === 'spectrometer') {
            if (xrayUrl && xrayUrl !== spectrometerUrl) URL.revokeObjectURL(xrayUrl);
            setXrayFile(null);
            setXrayUrl(null);
            setIsXrayScanned(false);
        } else if (mode === 'xray') {
            if (spectrometerUrl && spectrometerUrl !== xrayUrl) URL.revokeObjectURL(spectrometerUrl);
            setSpectrometerFile(null);
            setSpectrometerUrl(null);
            setIsSpectrometerScanned(false);
        }
    };

    const handleCombinedFileChange = (file) => {
        if (!file) return;
        if (spectrometerUrl) URL.revokeObjectURL(spectrometerUrl);
        if (xrayUrl && xrayUrl !== spectrometerUrl) URL.revokeObjectURL(xrayUrl);
        const url = URL.createObjectURL(file);
        setSpectrometerFile(file);
        setSpectrometerUrl(url);
        setIsSpectrometerScanned(false);
        setXrayFile(file);
        setXrayUrl(url);
        setIsXrayScanned(false);
    };

    const handleRemoveCombinedFile = () => {
        if (spectrometerUrl) URL.revokeObjectURL(spectrometerUrl);
        if (xrayUrl && xrayUrl !== spectrometerUrl) URL.revokeObjectURL(xrayUrl);
        setSpectrometerFile(null);
        setSpectrometerUrl(null);
        setIsSpectrometerScanned(false);
        setXrayFile(null);
        setXrayUrl(null);
        setIsXrayScanned(false);
    };

    const hasScannedCurrentFiles = (spectrometerFile && isSpectrometerScanned) || (xrayFile && isXrayScanned);
    const hasPendingScans = (spectrometerFile && !isSpectrometerScanned) || (xrayFile && !isXrayScanned);
    const [viewImageModal, setViewImageModal] = useState(null); // null | { images: [{ label, url }], activeIndex: 0 }
    const [zoomLevel, setZoomLevel] = useState(1);
    const imageContainerRef = useRef(null);

    const openImageViewer = (images, initialIndex = 0) => {
        if (!images || images.length === 0) return;
        setViewImageModal({ images, activeIndex: initialIndex });
        setZoomLevel(1);
    };

    // Charge summary modal — aggregated additions after each analysis
    const [chargeModal, setChargeModal] = useState(null); // null | { round }
    const [chargeModalSearch, setChargeModalSearch] = useState('');
    const [pCuInput, setPCuInput] = useState('');
    const [pCuError, setPCuError] = useState('');

    // Iron (FE) Pour Countdown Timer State & Persistence
    const POUR_TIMER_DEFAULT_MINUTES = 1;
    const [pourTimerMap, setPourTimerMap] = useState(() => {
        try {
            const saved = localStorage.getItem('metallurgy_fe_pour_timers');
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            return {};
        }
    });
    const [expiredModalBatchId, setExpiredModalBatchId] = useState(null);
    const [currentTime, setCurrentTime] = useState(Date.now());

    // Sync pourTimerMap with localStorage
    useEffect(() => {
        try {
            localStorage.setItem('metallurgy_fe_pour_timers', JSON.stringify(pourTimerMap));
        } catch (e) {
            console.error("Error saving pour timers to localStorage:", e);
        }
    }, [pourTimerMap]);

    // Synthetic audio alert for expired pour timer
    const playExpirationSound = useCallback(() => {
        try {
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sawtooth';
            osc.frequency.setValueAtTime(440, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.5);
            gain.gain.setValueAtTime(0.3, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.5);
        } catch (e) {
            console.error("Audio playback error:", e);
        }
    }, []);

    // 1-second ticker for active countdown timers (Iron FE module only)
    useEffect(() => {
        if (userModule !== 'FE') return;

        const interval = setInterval(() => {
            const now = Date.now();
            setCurrentTime(now);

            // Check running timers
            Object.keys(pourTimerMap).forEach(batchId => {
                const timerInfo = pourTimerMap[batchId];
                if (timerInfo && timerInfo.status === 'running') {
                    const elapsedSec = Math.floor((now - timerInfo.startTime) / 1000);
                    const totalSec = (timerInfo.durationMinutes || POUR_TIMER_DEFAULT_MINUTES) * 60;
                    if (elapsedSec >= totalSec) {
                        playExpirationSound();
                        setPourTimerMap(prev => ({
                            ...prev,
                            [batchId]: { ...prev[batchId], status: 'expired' }
                        }));
                        setExpiredModalBatchId(batchId);

                        // Notify backend server of batch expiration
                        const nowStr = new Date().toLocaleString('en-IN', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit', hour12: true
                        });
                        fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/batches/${batchId}/expire`, {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ expiredAt: nowStr })
                        })
                            .then(res => res.json())
                            .then(updatedBatch => {
                                setTransactions(prev => prev.map(t => t.id === batchId ? { ...t, status: 'Expired', expiredAt: nowStr } : t));
                            })
                            .catch(err => console.error("Error setting batch status to expired:", err));
                    }
                }
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [userModule, pourTimerMap, playExpirationSound]);

    const handleStartPourTimer = (batchId) => {
        const now = Date.now();
        const nowStr = new Date().toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
        setPourTimerMap(prev => ({
            ...prev,
            [batchId]: {
                startTime: now,
                durationMinutes: POUR_TIMER_DEFAULT_MINUTES,
                status: 'running',
                startedAtStr: nowStr
            }
        }));
    };

    const handleMarkBatchPoured = async (batchId) => {
        const currentUser = localStorage.getItem('currentUser') || 'admin';
        const nowStr = new Date().toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/batches/${batchId}/pour`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pouredBy: currentUser, pouredAt: nowStr })
            });
            const updated = await res.json();
            setTransactions(prev => prev.map(t =>
                t.id === batchId ? { ...t, ...updated, status: 'Poured' } : t
            ));
            setPourTimerMap(prev => ({
                ...prev,
                [batchId]: { ...prev[batchId], status: 'poured', pouredAt: nowStr }
            }));
        } catch (err) {
            console.error('Pour batch error:', err);
        }
    };

    const getRemainingSeconds = (batchId) => {
        const info = pourTimerMap[batchId];
        if (!info || !info.startTime) return 0;
        const elapsedSec = Math.floor((currentTime - info.startTime) / 1000);
        const totalSec = (info.durationMinutes || POUR_TIMER_DEFAULT_MINUTES) * 60;
        return Math.max(0, totalSec - elapsedSec);
    };

    const formatRemainingTime = (totalSeconds) => {
        const mins = Math.floor(totalSeconds / 60);
        const secs = totalSeconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };
    const [pCuSaving, setPCuSaving] = useState(false);
    const [pCuSaved, setPCuSaved] = useState(false);

    useEffect(() => {
        if (chargeModal && chargeModal.round) {
            // Load P Cu from the round data (already fetched from DB)
            setPCuInput(chargeModal.round.pCu || '');
            setPCuError('');
            setPCuSaved(false);
        }
    }, [chargeModal]);

    const handleSavePCu = async () => {
        if (!pCuInput.trim()) {
            setPCuError('P Cu cannot be empty');
            return;
        }
        setPCuError('');
        setPCuSaving(true);
        setPCuSaved(false);
        try {
            const batchId = activeTransactionId;
            const roundId = chargeModal.round.id;
            const res = await fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/batches/${batchId}/rounds/${roundId}/pcu`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pCu: pCuInput })
            });
            if (!res.ok) throw new Error('Failed to save P Cu');
            const updatedBatch = await res.json();
            // Sync local transactions state so modal shows saved value on re-open
            setTransactions(prev => prev.map(t =>
                t.id === batchId
                    ? { ...updatedBatch, itemName: t.itemName }
                    : t
            ));
            // Also update chargeModal round reference so saved value persists while modal is open
            setChargeModal(prev => ({
                ...prev,
                round: { ...prev.round, pCu: pCuInput }
            }));
            setPCuSaved(true);
        } catch (err) {
            console.error('P Cu save error:', err);
            setPCuError('Failed to save. Please try again.');
        } finally {
            setPCuSaving(false);
        }
    };


    // Snapshot detail modal — shows intermediate state at a specific step
    const [snapshotModal, setSnapshotModal] = useState(null); // null | { snapshot, roundNumber, allSnapshots }
    const [mismatchModal, setMismatchModal] = useState(null); // null | { reportType, detectedItem, expectedItem }
    const panRef = useRef({ dragging: false, startX: 0, startY: 0 });

    const openOcrModal = (itemSpecId, overrideTab = null, mode = 'preview') => {
        if (!spectrometerFile && !xrayFile) return;
        
        setOcrModal({
            step: mode,
            targetSpecId: itemSpecId,
            spectrometerFile,
            spectrometerUrl,
            xrayFile,
            xrayUrl,
            scannedInputs: mode === 'review' ? { ...currentInputs } : {},
            scannedSpectrometerInputs: null,
            scannedXrayInputs: null,
            previewZoom: 1,
            panX: 0,
            panY: 0,
            activeTab: overrideTab || (spectrometerFile ? 'spectrometer' : 'xray')
        });
    };

    const handlePanStart = useCallback((e) => {
        panRef.current = { dragging: true, startX: e.clientX, startY: e.clientY };
    }, []);

    const handlePanMove = useCallback((e) => {
        if (!panRef.current.dragging) return;
        const dx = e.clientX - panRef.current.startX;
        const dy = e.clientY - panRef.current.startY;
        panRef.current.startX = e.clientX;
        panRef.current.startY = e.clientY;
        setOcrModal(prev => {
            if (!prev) return prev;
            const zoom = prev.previewZoom || 1;
            // max pan range grows with zoom level
            const limit = (zoom - 1) * 300;
            const newX = Math.min(limit, Math.max(-limit, (prev.panX || 0) + dx));
            const newY = Math.min(limit, Math.max(-limit, (prev.panY || 0) + dy));
            return { ...prev, panX: newX, panY: newY };
        });
    }, []);

    const handlePanEnd = useCallback(() => {
        panRef.current.dragging = false;
    }, []);

    const closeOcrModal = () => {
        setOcrModal(null);
        setActiveOcrTooltip(null);
        setSwitchConfirmModal(null);
    };

    const runOcr = async () => {
        if (!ocrModal) return;
        const { targetSpecId, spectrometerFile, xrayFile } = ocrModal;
        setOcrModal(prev => ({ ...prev, step: 'scanning' }));

        const itemSpec = items.find(i => i.id === targetSpecId || i._id === targetSpecId);
        const compounds = itemSpec ? itemSpec.compounds : [];
        console.log("activeTransaction",activeTransaction)
        console.log("itemSpec",itemSpec)
        const itemCode = itemSpec?.itemCode || activeTransaction?.itemCode || '';

        try {
            const processFile = async (file, reportType) => {
                const reportLabel = reportType === 'xray' 
                    ? 'X-ray (XRF) fluorescence analysis report. CRITICAL: If the document contains both Spectrometer/OES and X-Ray/XRF tables or pages (combined report), specifically extract ONLY the X-Ray (XRF) composition table, NOT the Spectrometer table.' 
                    : 'spectrometer analysis report. CRITICAL: If the document contains both Spectrometer/OES and X-Ray/XRF tables or pages (combined report), specifically extract ONLY the Spectrometer (OES) composition table, NOT the X-Ray table.';
                const formData = new FormData();
                formData.append('image', file);
                formData.append(
                    'reportLabel',
                    `${reportLabel}`
                );
                if (itemCode) {
                    formData.append('itemCode', itemCode);
                }
                const res = await fetch(`${process.env.REACT_APP_ABSTRACT_SERVER_BASE_URL}/api/items/process-image`, {
                    method: 'POST',
                    body: formData
                });
                const data = await res.json();

                if (!res.ok) {
                    throw new Error(data.message || `Failed to process ${reportType} image via AI.`);
                }

                let cleanedOutput = data.output.replace(/```json/gi, '').replace(/```/gi, '').trim();
                const extractedData = JSON.parse(cleanedOutput);

                if (itemCode && extractedData.item_matches === false) {
                    const detected = extractedData.item_code || extractedData.item_name || 'Different Item';
                    const mismatchErr = new Error(`Report Mismatch: The uploaded ${reportType === 'xray' ? 'X-Ray' : 'Spectrometer'} report is for "${detected}", but the selected item is "${itemCode}".`);
                    mismatchErr.isMismatch = true;
                    mismatchErr.mismatchData = {
                        reportType: reportType === 'xray' ? 'X-Ray' : 'Spectrometer',
                        detectedItem: detected,
                        expectedItem: itemCode
                    };
                    throw mismatchErr;
                }

                const elementsList = Array.isArray(extractedData) 
                    ? extractedData 
                    : (extractedData.elements || []);

                const fileInputs = {};
                compounds.forEach(comp => { fileInputs[comp.element] = ''; });

                elementsList.forEach(item => {
                    const elementName = item.element_name.toUpperCase().trim();
                    let val = parseFloat(item.value);

                    if (isNaN(val)) return;

                    for (let comp of compounds) {
                        const matchCode = comp.element.match(/\(([A-Za-z0-9,]+)\)/);
                        const symbol = matchCode ? matchCode[1].toUpperCase() : comp.element.split(' ')[0].toUpperCase();
                        if (symbol === elementName) {
                            fileInputs[comp.element] = val;
                            break;
                        }
                    }
                });
                return fileInputs;
            };

            const promises = [];
            if (spectrometerFile) {
                promises.push(
                    processFile(spectrometerFile, 'spectrometer').catch(err => {
                        if (err.isMismatch) throw err;
                        console.warn('Spectrometer parsing error:', err);
                        return null;
                    })
                );
            } else {
                promises.push(Promise.resolve(null));
            }
            
            if (xrayFile) {
                promises.push(
                    processFile(xrayFile, 'xray').catch(err => {
                        if (err.isMismatch) throw err;
                        console.warn('X-Ray parsing error:', err);
                        return null;
                    })
                );
            } else {
                promises.push(Promise.resolve(null));
            }

            const [scannedSpectrometerInputs, scannedXrayInputs] = await Promise.all(promises);

            if (spectrometerFile && xrayFile && !scannedSpectrometerInputs && !scannedXrayInputs) {
                throw new Error('Failed to extract chemical composition values from the uploaded report(s). Please verify file quality or enter values manually.');
            } else if (spectrometerFile && !xrayFile && !scannedSpectrometerInputs) {
                throw new Error('Failed to extract Spectrometer values from the uploaded file.');
            } else if (xrayFile && !spectrometerFile && !scannedXrayInputs) {
                throw new Error('Failed to extract X-Ray values from the uploaded file.');
            }

            const mergedInputs = {};
            const spectrometerReasons = {};
            compounds.forEach(comp => { 
                mergedInputs[comp.element] = ''; 
                const xrayVal = scannedXrayInputs ? scannedXrayInputs[comp.element] : '';
                const specVal = scannedSpectrometerInputs ? scannedSpectrometerInputs[comp.element] : '';
                
                if (xrayVal !== '' && typeof xrayVal === 'number') {
                    if (xrayVal <= 0.09) {
                        if (specVal !== '' && Number(specVal) === 0 && xrayVal > 0) {
                            mergedInputs[comp.element] = xrayVal;
                        } else if (specVal !== '') {
                            mergedInputs[comp.element] = specVal;
                            spectrometerReasons[comp.element] = `Spectrometer value (${specVal}%) was selected because X-Ray value (${xrayVal}%) is ≤ 0.09%`;
                        } else {
                            mergedInputs[comp.element] = xrayVal;
                        }
                    } else {
                        mergedInputs[comp.element] = xrayVal;
                    }
                } else if (specVal !== '') {
                    mergedInputs[comp.element] = specVal;
                    if (scannedXrayInputs) {
                        spectrometerReasons[comp.element] = `Spectrometer value (${specVal}%) was selected because no X-Ray value was detected`;
                    }
                }
            });

            setOcrModal(prev => ({ 
                ...prev, 
                step: 'review', 
                scannedSpectrometerInputs,
                scannedXrayInputs,
                scannedInputs: mergedInputs,
                spectrometerReasons
            }));

        } catch (err) {
            console.error('AI Vision Error:', err);
            closeOcrModal();
            if (err.isMismatch && err.mismatchData) {
                setMismatchModal(err.mismatchData);
            } else {
                alert(err.message || 'Error parsing lab report image via AI.');
            }
        }
    };

    const getSpectrometerConsideredReason = (element) => {
        const currentVal = ocrModal?.scannedInputs?.[element];
        const specVal = ocrModal?.scannedSpectrometerInputs?.[element];
        const xrayVal = ocrModal?.scannedXrayInputs?.[element];

        if (currentVal === '' || currentVal === null || currentVal === undefined) {
            return null;
        }

        const isSpecActive = specVal !== '' && specVal !== undefined && specVal !== null && parseFloat(currentVal) === parseFloat(specVal);
        const isXrayActive = xrayVal !== '' && xrayVal !== undefined && xrayVal !== null && parseFloat(currentVal) === parseFloat(xrayVal);

        if (!isSpecActive) {
            return null;
        }

        if (isXrayActive && parseFloat(specVal) === parseFloat(xrayVal)) {
            return null;
        }

        if (ocrModal?.spectrometerFile && ocrModal?.xrayFile) {
            if (xrayVal !== '' && typeof xrayVal === 'number') {
                if (xrayVal <= 0.09) {
                    return `Spectrometer value (${specVal}%) was selected because X-Ray value (${xrayVal}%) is ≤ 0.09%`;
                } else {
                    return `Spectrometer value (${specVal}%) was manually chosen over X-Ray value (${xrayVal}%)`;
                }
            } else {
                return `Spectrometer value (${specVal}%) was selected because no X-Ray value was detected`;
            }
        }
        return null;
    };

    const handleRequestValueSwitch = (element, source, newValue) => {
        if (newValue === '' || newValue === null || newValue === undefined || isNaN(parseFloat(newValue))) {
            return;
        }
        const currentVal = ocrModal?.scannedInputs?.[element];
        if (currentVal !== '' && currentVal !== null && currentVal !== undefined && parseFloat(currentVal) === parseFloat(newValue)) {
            return;
        }
        setSwitchConfirmModal({
            element,
            source,
            value: newValue,
            currentValue: currentVal ?? ''
        });
    };

    const handleConfirmValueSwitch = () => {
        if (!switchConfirmModal) return;
        const { element, value } = switchConfirmModal;
        handleOcrInputChange(element, value);
        setSwitchConfirmModal(null);
    };

    const handleCancelValueSwitch = () => {
        setSwitchConfirmModal(null);
    };

    const handleOcrInputChange = (element, value) => {
        setOcrModal(prev => ({ ...prev, scannedInputs: { ...prev.scannedInputs, [element]: value }, acknowledgedZeros: false }));
    };

    const confirmOcrValues = (andRunAnalysis = false) => {
        if (!ocrModal) return;
        const { scannedInputs } = ocrModal;

        const updatedInputs = { ...currentInputs };
        Object.entries(scannedInputs || {}).forEach(([elem, val]) => {
            if (val !== '' && val !== null && val !== undefined) {
                updatedInputs[elem] = val;
            }
        });
        setCurrentInputs(updatedInputs);

        if (ocrModal.spectrometerFile) setIsSpectrometerScanned(true);
        if (ocrModal.xrayFile) setIsXrayScanned(true);

        if (activeTransaction && andRunAnalysis) {
            setOcrModal(prev => ({ ...prev, step: 'analyzing' }));
            setTimeout(() => processIterationWithInputs(updatedInputs), 0);
        } else {
            setOcrModal(null);
        }
    };



    const handleLogout = () => {
        localStorage.clear();
        navigate('/login');
    };

    const handleAccessItems = (e) => {
        e.preventDefault();
        if (secretCode === '1234' || secretCode === 'admin123') {
            navigate('/item-management');
        } else {
            setError('Invalid Access Code. Access Denied.');
        }
    };

    const handleInputChange = (element, value) => {
        setCurrentInputs(prev => ({ ...prev, [element]: value }));
    };

    const handleItemSelection = (itemId) => {
        setSelectedItem(itemId);
        const itemSpec = items.find(i => i.id === itemId);
        if (itemSpec) {
            const inputs = {};
            itemSpec.compounds.forEach(c => inputs[c.element] = '');
            setCurrentInputs(inputs);
        }
    };

    const activeTransaction = transactions.find(t => t.id === activeTransactionId);

    const processIterationWithInputs = (inputs) => {
        const resolvedInputs = inputs || currentInputs;
        if (isCreatingNew && (!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === ''))) return;

        setIsAnalyzing(true);
        const currentSpecId = isCreatingNew ? selectedItem : activeTransaction.itemSpecId;
        const currentWeight = isCreatingNew ? parseFloat(baseWeight) : activeTransaction.baseWeight;
        const itemSpec = items.find(i => i.id === currentSpecId);

        setTimeout(async () => {
            try {
                const cuCompound = (itemSpec?.compounds || []).find(c => isCopperElement(c.element));
                // Exception usecase: item setup with no max value only for copper in item spec
                const isNoMaxCu = cuCompound &&
                    (cuCompound.spec_max === null || cuCompound.spec_max === undefined || cuCompound.spec_max === '' || parseFloat(cuCompound.spec_max) === 0) &&
                    (cuCompound.target_max === null || cuCompound.target_max === undefined || cuCompound.target_max === '' || parseFloat(cuCompound.target_max) === 0);

                let chosenCuActual = null;
                let cuAdjustmentDetails = null;

                if (cuCompound && isNoMaxCu) {
                    const actualCu = parseFloat(resolvedInputs[cuCompound.element]) || 0;
                    
                    // Sum of actuals of all other elements except copper
                    const sumOtherActuals = (itemSpec?.compounds || [])
                        .filter(c => !isCopperElement(c.element))
                        .reduce((sum, c) => sum + (parseFloat(resolvedInputs[c.element]) || 0), 0);

                    // Subtract it from 100
                    const computedCu = Math.max(0, parseFloat((100 - sumOtherActuals).toFixed(4)));

                    // Min of the spec (target_min if defined, otherwise spec_min)
                    const cuMin = (cuCompound.target_min !== undefined && cuCompound.target_min !== null && cuCompound.target_min !== '')
                        ? parseFloat(cuCompound.target_min)
                        : parseFloat(cuCompound.spec_min || 0);

                    const diffActual = Math.abs(actualCu - cuMin);
                    const diffComputed = Math.abs(computedCu - cuMin);

                    // Consider the one which is nearest to min of the spec
                    chosenCuActual = diffComputed < diffActual ? computedCu : actualCu;

                    cuAdjustmentDetails = {
                        element: cuCompound.element,
                        actualCu,
                        sumOtherActuals: parseFloat(sumOtherActuals.toFixed(4)),
                        computedCu,
                        cuMin,
                        diffActual: parseFloat(diffActual.toFixed(4)),
                        diffComputed: parseFloat(diffComputed.toFixed(4)),
                        chosenCu: chosenCuActual,
                        isComputedChosen: diffComputed < diffActual
                    };

                    console.log("[Copper No-Max Exception Applied]", cuAdjustmentDetails);
                }

                const inputCompounds = itemSpec.compounds.map(comp => {
                    const setting = elementSettings.find(s => s.element === comp.element);
                    let actualVal = parseFloat(resolvedInputs[comp.element]) || 0;
                    if (isCopperElement(comp.element) && isNoMaxCu && chosenCuActual !== null) {
                        actualVal = chosenCuActual;
                    }
                    return {
                        element: comp.element,
                        spec_min: comp.spec_min,
                        spec_max: comp.spec_max,
                        target_min: comp.target_min,
                        target_max: comp.target_max,
                        loss_factor: setting ? setting.loss_factor : 0,
                        actual: actualVal
                    };
                });
                console.log("Input Compounds for Analysis:", inputCompounds);
                console.log("itemSpec.compounds", itemSpec.compounds);

                let computedMaxChargeWeight = null;
                if (maxChargeWeightLbs !== '') {
                    computedMaxChargeWeight = parseFloat(maxChargeWeightLbs) || 0;
                } else if (maxChargeWeightPercent !== '') {
                    const pct = parseFloat(maxChargeWeightPercent) || 0;
                    computedMaxChargeWeight = currentWeight + (currentWeight * pct / 100);
                }

                const analyzeEndpoint = userModule === 'FE' ? '/api/engine/analyze-iron' : '/api/engine/analyze';
                const response = await fetch(`${process.env.REACT_APP_ABSTRACT_SERVER_BASE_URL}${analyzeEndpoint}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        baseWeight: currentWeight,
                        max_charge_weight: computedMaxChargeWeight,
                        currentCompounds: inputCompounds
                    })
                });

                if (!response.ok) {
                    throw new Error(`API analysis failed with status: ${response.status}`);
                }

                const result = await response.json();
                const { overallStatus, results, predictedWeight, totalNewWeightAdded, additions, snapshots } = result;

                console.log("overallStatus", overallStatus)
                console.log("results", results)
                console.log("predictedWeight", predictedWeight)
                console.log("totalNewWeightAdded", totalNewWeightAdded)
                console.log("additions", additions)
                console.log("snapshots", snapshots)
                const currentUser = localStorage.getItem('currentUser') || 'admin';
                // ensure it's always stored for future rounds
                if (!localStorage.getItem('currentUser')) localStorage.setItem('currentUser', 'admin');
                const nowStr = new Date().toLocaleString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', hour12: true
                });

                let finalSpectrometerImage = '';
                let finalXrayImage = '';

                if (spectrometerFile) {
                    try {
                        const formData = new FormData();
                        formData.append('file', spectrometerFile);
                        const uploadRes = await fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/upload/lab-report`, {
                            method: 'POST',
                            body: formData
                        });
                        if (uploadRes.ok) {
                            const uploadData = await uploadRes.json();
                            finalSpectrometerImage = uploadData?.data?.[0]?.mediaLink || '';
                            console.log("Uploaded Spectrometer Image URL:", finalSpectrometerImage);
                        } else {
                            console.error("Failed to upload spectrometer report image.");
                        }
                    } catch (e) {
                        console.error("Error uploading spectrometer image:", e);
                    }
                }

                if (xrayFile) {
                    if (xrayFile === spectrometerFile && finalSpectrometerImage) {
                        finalXrayImage = finalSpectrometerImage;
                        console.log("Reusing Combined Report URL for X-Ray:", finalXrayImage);
                    } else {
                        try {
                            const formData = new FormData();
                            formData.append('file', xrayFile);
                            const uploadRes = await fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/upload/lab-report`, {
                                method: 'POST',
                                body: formData
                            });
                            if (uploadRes.ok) {
                                const uploadData = await uploadRes.json();
                                finalXrayImage = uploadData?.data?.[0]?.mediaLink || '';
                                console.log("Uploaded X-Ray Image URL:", finalXrayImage);
                            } else {
                                console.error("Failed to upload X-ray report image.");
                            }
                        } catch (e) {
                            console.error("Error uploading X-ray image:", e);
                        }
                    }
                }

                const newRound = {
                    id: Date.now().toString(),
                    roundNumber: isCreatingNew ? 1 : activeTransaction.rounds.length + 1,
                    timestamp: new Date()?.toLocaleString(),
                    createdAt: nowStr,
                    createdBy: currentUser,
                    overallStatus,
                    baseWeight: currentWeight,
                    predictedWeight,
                    totalNewWeightAdded,
                    data: results,
                    additions: additions || [],
                    snapshots: snapshots || [],
                    spectrometerImage: finalSpectrometerImage,
                    xrayImage: finalXrayImage,
                    cuAdjustment: cuAdjustmentDetails
                };

                if (isCreatingNew) {
                    const userModule = localStorage.getItem('userModule') || 'CU';
                    const userId = localStorage.getItem('userId') || localStorage.getItem('currentUser') || 'admin';
                    const newTxn = {
                        itemSpecId: itemSpec.id,
                        itemName: itemSpec.name,
                        baseWeight: predictedWeight,
                        initialWeight: currentWeight,
                        maxChargeWeight: computedMaxChargeWeight,
                        status: overallStatus,
                        createdBy: currentUser,
                        userId: userId,
                        createdAt: nowStr,
                        module: userModule,
                        rounds: [newRound]
                    };

                    fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/batches`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-module': userModule,
                            'x-user-id': userId
                        },
                        body: JSON.stringify(newTxn)
                    })
                        .then(res => res.json())
                        .then(savedData => {
                            if (savedData.message) {
                                alert(`Error creating batch: ${savedData.message}`);
                                return;
                            }
                            const mapped = {
                                ...savedData,
                                itemName: itemSpec.name // keep immediate UI mapping fast
                            }
                            setTransactions([mapped, ...transactions]);
                            setActiveTransactionId(savedData.id);
                            setIsCreatingNew(false);
                        });
                } else {
                    fetch(`${process.env.REACT_APP_SERVER_BASE_URL}/api/batches/${activeTransactionId}/rounds`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(newRound)
                    })
                        .then(res => res.json())
                        .then(updatedBatch => {
                            setTransactions(transactions.map(txn => {
                                if (txn.id === activeTransactionId) {
                                    return {
                                        ...updatedBatch,
                                        itemName: txn.itemName
                                    };
                                }
                                return txn;
                            }));
                            // Collapse all previous rounds, expand the newly added one
                            setCollapsedRounds(prev => {
                                const next = { ...prev };
                                // collapse all existing rounds
                                Object.keys(next).forEach(k => { next[k] = true; });
                                // expand the new round
                                next[newRound.id] = false;
                                return next;
                            });
                        });
                }

                // reset inputs for next round
                const inputs = {};
                itemSpec.compounds.forEach(c => inputs[c.element] = '');
                setCurrentInputs(inputs);
                setIsAnalyzing(false);

                if (spectrometerUrl) URL.revokeObjectURL(spectrometerUrl);
                if (xrayUrl) URL.revokeObjectURL(xrayUrl);
                setSpectrometerFile(null);
                setSpectrometerUrl(null);
                setXrayFile(null);
                setXrayUrl(null);
                closeOcrModal();
                // Auto-open charge summary modal after 3 seconds
                setTimeout(() => {
                    setChargeModal({ round: newRound });
                    setChargeModalSearch('');
                }, 3000);
            } catch (err) {
                console.error("Analysis API Error:", err);
                alert(`Analysis API Error: ${err.message}`);
                setIsAnalyzing(false);
            }
        }, 800);
    };

    const processIteration = () => processIterationWithInputs(currentInputs);

    const handleSelectTransaction = (id) => {
        setActiveTransactionId(id);
        setIsCreatingNew(false);
        const txn = transactions.find(t => t.id === id);
        if (txn) {
            const spec = items.find(i => i.id === txn.itemSpecId);
            const inputs = {};
            if (spec) spec.compounds.forEach(c => inputs[c.element] = '');
            setCurrentInputs(inputs);
            // Auto-collapse all rounds except the latest when switching batch
            const initialCollapsed = {};
            txn.rounds.forEach((r, idx) => {
                initialCollapsed[r.id] = idx !== txn.rounds.length - 1; // collapse all except latest
            });
            setCollapsedRounds(initialCollapsed);
        }
        if (spectrometerUrl) URL.revokeObjectURL(spectrometerUrl);
        if (xrayUrl) URL.revokeObjectURL(xrayUrl);
        setSpectrometerFile(null);
        setSpectrometerUrl(null);
        setXrayFile(null);
        setXrayUrl(null);
        setViewMode('reports');
    };

    const handleStartNewTransaction = () => {
        setActiveTransactionId(null);
        setIsCreatingNew(true);
        setSelectedItem('');
        setBaseWeight('');
        setMaxChargeWeightLbs('');
        setMaxChargeWeightPercent('');
        setCurrentInputs({});
        if (spectrometerUrl) URL.revokeObjectURL(spectrometerUrl);
        if (xrayUrl) URL.revokeObjectURL(xrayUrl);
        setSpectrometerFile(null);
        setSpectrometerUrl(null);
        setXrayFile(null);
        setXrayUrl(null);
        setViewMode('reports');
        setSelectedCustomer('');
        setCustomerSearch('');
        setItemSearch('');
        setCustomerDropdownOpen(false);
        setItemDropdownOpen(false);
    };

    const prepareChartData = () => {
        if (!activeTransaction) return [];

        // Transform rounds into a format suitable for trend lines
        return activeTransaction.rounds.map(round => {
            const row = { round: `R${round.roundNumber}` };
            round.data.forEach(item => {
                row[item.element] = item.actual;
                row[`${item.element}_min`] = item.min;
                row[`${item.element}_max`] = item.max;
            });
            return row;
        });
    };

    const getElementTendencyData = () => {
        if (!activeTransaction || activeTransaction.rounds.length === 0) return [];
        const latestRound = activeTransaction.rounds[activeTransaction.rounds.length - 1];

        return latestRound.data.map(item => {
            const midpoint = (item.min + item.max) / 2;
            const deviation = midpoint === 0 ? 0 : ((item.actual - midpoint) / midpoint) * 100;
            return {
                name: item.element,
                actual: item.actual,
                min: item.min,
                max: item.max,
                deviation: parseFloat(deviation.toFixed(2))
            };
        });
    };

    // Derived values for pagination
    const indexOfLastTransaction = currentPage * transactionsPerPage;
    const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
    const currentTransactions = transactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
    const totalPages = Math.ceil(transactions.length / transactionsPerPage);
    // const totalPages = 20; // uncomment to test with many pages

    const getPaginationRange = () => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        if (currentPage <= 4) {
            return [1, 2, 3, 4, 5, '...', totalPages];
        } else if (currentPage >= totalPages - 3) {
            return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        } else {
            return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
        }
    };

    return (
        <div className="dashboard-container">
            <nav className="dashboard-nav glass-panel">
                <div className="nav-brand">
                    <img src={userModule === 'FE' ? "ladle-logic-iron.png" : "logo.png"} className="nav-logo" alt="Logo" />
                    <h2 className="text-gradient m-0">
                        Ladle Logic - Charge Calculator {localStorage.getItem('userModule') ? `(${localStorage.getItem('userModule') === 'FE' ? 'Iron' : 'Copper'})` : ''}
                    </h2>
                </div>
                <div className="nav-actions">
                    {accessibleModules.length > 1 && (
                        <button className="btn-secondary" onClick={() => navigate('/select-module')}>
                            <Box size={18} /> Switch Domain
                        </button>
                    )}
                    {!(userRole === 'supervisor' || userRole === 'shop floor operator' || userRole === 'operator') && (
                        <button className="btn-secondary" onClick={() => setShowSecretModal(true)}>
                            <PackageSearch size={18} /> Master Specifications
                        </button>
                    )}
                    <button className="btn-secondary logout-btn" onClick={handleLogout}>
                        <LogOut size={18} /> Disconnect
                    </button>
                </div>
            </nav>

            <main className="dashboard-content flex-grow p-0">
                <div className="dashboard-grid layout-wide">
                    {/* LEFTSIDE: History and Transactions Nav */}
                    <div className="history-sidebar glass-panel">
                        <div className="sidebar-header flex justify-between items-center mb-6">
                            <h3 className="panel-title text-primary m-0 flex items-center gap-2"><History size={20} /> Transactions</h3>
                            {!(userRole === 'shop floor operator' || userRole === 'operator') && (
                                <button className="btn-primary flex items-center gap-2" style={{ padding: '8px 16px', fontSize: '14px', borderRadius: '8px' }} onClick={handleStartNewTransaction} title="New Batch">
                                    <PlusCircle size={16} /> <span>New Batch</span>
                                </button>
                            )}
                        </div>

                        <div className="transaction-list mt-6 overflow-y-auto">
                            {transactions.length === 0 ? (
                                <p className="text-secondary text-sm text-center py-8">No transactions found.</p>
                            ) : (
                                currentTransactions.map(txn => (
                                    <motion.div
                                        key={txn.id}
                                        className={`txn-card clickable ${activeTransactionId === txn.id ? 'active' : ''}`}
                                        onClick={() => handleSelectTransaction(txn.id)}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                    >
                                        <div className="txn-info">
                                            <span className="txn-id">{txn.id}</span>
                                            <span className="txn-target">{txn.itemName}</span>
                                            <span className="txn-meta">
                                                {txn.rounds.length} Rounds
                                                {/* {txn.maxChargeWeight && ` • Max: ${txn.maxChargeWeight.toLocaleString()} lbs`} */}
                                            </span>
                                            <span className="txn-meta" style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                                                {txn.createdAt} • {txn.createdBy || 'Unknown'}
                                            </span>
                                        </div>
                                        <div className="txn-status-indicator">
                                            {txn.status === 'In Spec' ? (
                                                <CheckCircle size={18} color="#10b981" />
                                            ) : txn.status === 'Poured' ? (
                                                <CheckCircle size={18} color="#0ea5e9" />
                                            ) : txn.status === 'Expired' ? (
                                                <AlertTriangle size={18} color="#0ea5e9" />
                                            ) : txn.status === 'Manually Closed' ? (
                                                <X size={18} color="#94a3b8" />
                                            ) : (
                                                <AlertTriangle size={18} color="#f59e0b" />
                                            )}
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex justify-between items-center mt-4 px-2">
                                <button
                                    className="btn-secondary flex items-center justify-center gap-1"
                                    style={{ padding: '4px', fontSize: '12px', borderRadius: '6px', opacity: currentPage === 1 ? 0.5 : 1 }}
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft size={16} />
                                </button>

                                <div className="flex gap-1 items-center justify-center">
                                    {getPaginationRange().map((page, index) => (
                                        <button
                                            key={index}
                                            onClick={() => page !== '...' && setCurrentPage(page)}
                                            disabled={page === '...'}
                                            style={{
                                                padding: '4px 8px',
                                                fontSize: '12px',
                                                borderRadius: '4px',
                                                backgroundColor: currentPage === page ? 'var(--primary-color)' : 'transparent',
                                                color: currentPage === page ? 'white' : 'var(--text-secondary)',
                                                border: currentPage === page ? 'none' : (page === '...' ? 'none' : '1px solid var(--border-color)'),
                                                cursor: page === '...' ? 'default' : 'pointer',
                                                minWidth: '28px'
                                            }}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    className="btn-secondary flex items-center justify-center gap-1"
                                    style={{ padding: '4px', fontSize: '12px', borderRadius: '6px', opacity: currentPage === totalPages ? 0.5 : 1 }}
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* RIGHTSIDE: Transaction Details & Round Execution */}
                    <div className="transaction-workspace glass-panel">
                        {isCreatingNew ? (
                            (userRole === 'shop floor operator' || userRole === 'operator') ? (
                                <div className="workspace-inner text-center" style={{ padding: '80px 20px', textAlign: 'center' }}>
                                    <History size={48} color="var(--primary-color)" style={{ margin: '0 auto 16px', opacity: 0.7 }} />
                                    <h3 style={{ margin: '0 0 8px', fontSize: '20px' }}>Select a Transaction</h3>
                                    <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                                        As a Shop Floor Operator, select a transaction batch from the list on the left to view batch reports and test results.
                                    </p>
                                </div>
                            ) : (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="workspace-inner new-batch">
                                    <div className="workspace-header mb-8">
                                        <h2 className="text-primary m-0">Initialize New Batch</h2>
                                        <p className="text-secondary">Setup a new recycling transaction and calculate primary yield adjustments.</p>
                                    </div>

                                    {/* Derived lists for customer + item dropdowns */}
                                    {(() => {
                                        const uniqueCustomers = [...new Set(
                                            items
                                                .map(i => (i.customerName || '').trim())
                                                .filter(Boolean)
                                        )].sort();

                                        const filteredCustomers = uniqueCustomers.filter(c =>
                                            c.toLowerCase().includes(customerSearch.toLowerCase())
                                        );

                                        const itemsForCustomer = selectedCustomer
                                            ? items.filter(i => (i.customerName || '').trim() === selectedCustomer)
                                            : items;

                                        const filteredItems = itemsForCustomer.filter(i =>
                                            `${i.itemCode} ${i.name}`.toLowerCase().includes(itemSearch.toLowerCase())
                                        );

                                        const selectedItemObj = items.find(i => i.id === selectedItem);

                                        return (
                                            <div className="space-y-6">
                                                <div className="form-grid-2">
                                                    {/* Customer Name searchable dropdown */}
                                                    <div className="form-group">
                                                        <label>Customer Name</label>
                                                        <div className="searchable-dropdown" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setCustomerDropdownOpen(false); }} tabIndex={-1}>
                                                            <div
                                                                className={`input-field sdd-trigger ${customerDropdownOpen ? 'sdd-open' : ''}`}
                                                                onClick={() => { setCustomerDropdownOpen(o => !o); setCustomerSearch(''); }}
                                                            >
                                                                <span className={selectedCustomer ? 'sdd-value' : 'sdd-placeholder'}>
                                                                    {selectedCustomer || '-- Select customer --'}
                                                                </span>
                                                                <svg className={`sdd-arrow ${customerDropdownOpen ? 'sdd-arrow-up' : ''}`} viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1l4 4 4-4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                            </div>
                                                            {customerDropdownOpen && (
                                                                <div className="sdd-panel">
                                                                    <div className="sdd-search-wrap">
                                                                        <input
                                                                            autoFocus
                                                                            className="sdd-search"
                                                                            placeholder="Search customer..."
                                                                            value={customerSearch}
                                                                            onChange={e => setCustomerSearch(e.target.value)}
                                                                            onClick={e => e.stopPropagation()}
                                                                        />
                                                                    </div>
                                                                    <div className="sdd-options">
                                                                        {selectedCustomer && (
                                                                            <div
                                                                                className="sdd-option sdd-clear"
                                                                                onMouseDown={() => { setSelectedCustomer(''); setSelectedItem(''); setCurrentInputs({}); setCustomerDropdownOpen(false); setCustomerSearch(''); }}
                                                                            >
                                                                                ✕ Clear filter
                                                                            </div>
                                                                        )}
                                                                        {filteredCustomers.length === 0 ? (
                                                                            <div className="sdd-empty">No customers found</div>
                                                                        ) : filteredCustomers.map(c => (
                                                                            <div
                                                                                key={c}
                                                                                className={`sdd-option ${selectedCustomer === c ? 'sdd-selected' : ''}`}
                                                                                onMouseDown={() => { setSelectedCustomer(c); setSelectedItem(''); setCurrentInputs({}); setCustomerDropdownOpen(false); setCustomerSearch(''); setItemSearch(''); }}
                                                                            >
                                                                                {c}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Item (Target Specification) searchable dropdown */}
                                                    <div className="form-group">
                                                        <label>Target Specification</label>
                                                        <div className="searchable-dropdown" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setItemDropdownOpen(false); }} tabIndex={-1}>
                                                            <div
                                                                className={`input-field sdd-trigger ${itemDropdownOpen ? 'sdd-open' : ''}`}
                                                                onClick={() => { setItemDropdownOpen(o => !o); setItemSearch(''); }}
                                                            >
                                                                <span className={selectedItemObj ? 'sdd-value' : 'sdd-placeholder'}>
                                                                    {selectedItemObj ? `${selectedItemObj.itemCode} - ${selectedItemObj.name}` : '-- Select target alloy --'}
                                                                </span>
                                                                <svg className={`sdd-arrow ${itemDropdownOpen ? 'sdd-arrow-up' : ''}`} viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1l4 4 4-4" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                            </div>
                                                            {itemDropdownOpen && (
                                                                <div className="sdd-panel">
                                                                    <div className="sdd-search-wrap">
                                                                        <input
                                                                            autoFocus
                                                                            className="sdd-search"
                                                                            placeholder="Search alloy..."
                                                                            value={itemSearch}
                                                                            onChange={e => setItemSearch(e.target.value)}
                                                                            onClick={e => e.stopPropagation()}
                                                                        />
                                                                    </div>
                                                                    <div className="sdd-options">
                                                                        {filteredItems.length === 0 ? (
                                                                            <div className="sdd-empty">
                                                                                {selectedCustomer ? `No specs for "${selectedCustomer}"` : 'No alloys found'}
                                                                            </div>
                                                                        ) : filteredItems.map(item => (
                                                                            <div
                                                                                key={item.id}
                                                                                className={`sdd-option ${selectedItem === item.id ? 'sdd-selected' : ''}`}
                                                                                onMouseDown={() => { handleItemSelection(item.id); setItemDropdownOpen(false); setItemSearch(''); }}
                                                                            >
                                                                                <span className="sdd-item-code">{item.itemCode}</span>
                                                                                <span className="sdd-item-name">{item.name}</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Charge Weight Configuration Group */}
                                                <div className="form-group-weight-container">
                                                    <div className="weight-group-header">
                                                        <label className="weight-group-title">Charge Weight Configuration</label>
                                                        <button
                                                            type="button"
                                                            className="btn-reset-tiny"
                                                            onClick={() => {
                                                                setMaxChargeWeightLbs('');
                                                                setMaxChargeWeightPercent('');
                                                                setBaseWeight('')
                                                            }}
                                                        >
                                                            Reset
                                                        </button>
                                                    </div>
                                                    <div className="weight-group-inputs">
                                                        {/* Base Weight */}
                                                        <div className="form-group">
                                                            <label>Initial Charge Weight (lbs)</label>
                                                            <input
                                                                type="number"
                                                                className="input-field"
                                                                value={baseWeight}
                                                                onChange={(e) => setBaseWeight(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className={`form-group ${maxChargeWeightPercent !== '' ? 'opacity-50' : ''}`}>
                                                            <label>Max Charge Weight (lbs)</label>
                                                            <input
                                                                type="number"
                                                                className="input-field"
                                                                value={maxChargeWeightLbs}
                                                                disabled={maxChargeWeightPercent !== ''}
                                                                onChange={(e) => setMaxChargeWeightLbs(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className={`form-group ${maxChargeWeightLbs !== '' ? 'opacity-50' : ''}`}>
                                                            <label>Max Charge Weight (%)</label>
                                                            <input
                                                                type="number"
                                                                className="input-field"
                                                                value={maxChargeWeightPercent}
                                                                disabled={maxChargeWeightLbs !== ''}
                                                                onChange={(e) => setMaxChargeWeightPercent(e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    <div className="form-group mt-6">
                                        <div className="flex justify-between items-center mb-2">
                                            <label className="m-0 block font-semibold text-slate-800">0th Iteration Test Results (% Found)</label>
                                            <span className="text-xs text-slate-500 font-medium">Select report upload method</span>
                                        </div>

                                        {/* Report Upload Mode Selector */}
                                        <div className="report-mode-toolbar mb-3">
                                            <div className="report-mode-tabs">
                                                <button
                                                    type="button"
                                                    className={`report-mode-tab ${uploadReportMode === 'both' ? 'active' : ''}`}
                                                    onClick={() => handleSwitchReportMode('both')}
                                                >
                                                    <Layers size={14} />
                                                    <span>Both (Spectro + X-Ray)</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`report-mode-tab ${uploadReportMode === 'spectrometer' ? 'active' : ''}`}
                                                    onClick={() => handleSwitchReportMode('spectrometer')}
                                                >
                                                    <FileSpreadsheet size={14} />
                                                    <span>Only Spectro</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`report-mode-tab ${uploadReportMode === 'xray' ? 'active' : ''}`}
                                                    onClick={() => handleSwitchReportMode('xray')}
                                                >
                                                    <Zap size={14} />
                                                    <span>Only X-Ray</span>
                                                </button>
                                            </div>

                                            {uploadReportMode === 'both' && (
                                                <div className="report-subtype-switch">
                                                    <button
                                                        type="button"
                                                        className={`report-subtype-btn ${bothUploadSubtype === 'combined' ? 'active' : ''}`}
                                                        onClick={() => setBothUploadSubtype('combined')}
                                                    >
                                                        1 Combined File
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`report-subtype-btn ${bothUploadSubtype === 'separate' ? 'active' : ''}`}
                                                        onClick={() => setBothUploadSubtype('separate')}
                                                    >
                                                        2 Separate Files
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="dual-upload-grid">
                                            {/* Combined Report Card (when Both -> 1 Combined File is chosen) */}
                                            {uploadReportMode === 'both' && bothUploadSubtype === 'combined' && (
                                                <div className={`dual-upload-card combined-card ${isCombinedFile ? 'has-file' : ''} ${(!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '')) ? 'disabled' : ''}`}>
                                                    <div className="upload-card-header">
                                                        <div className="upload-card-badge combined-badge">
                                                            <Layers size={15} />
                                                            <span>Combined Report (Spectro + X-Ray)</span>
                                                        </div>
                                                        {(isSpectrometerScanned && isXrayScanned) ? (
                                                            <span className="upload-status-chip">
                                                                <CheckCircle size={13} /> Scanned
                                                            </span>
                                                        ) : isCombinedFile ? (
                                                            <span className="upload-status-chip" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #cbd5e1' }}>
                                                                <UploadCloud size={13} /> Uploaded
                                                            </span>
                                                        ) : null}
                                                    </div>

                                                    <div className="upload-card-body">
                                                        {isCombinedFile ? (
                                                            <div className="upload-preview-wrap">
                                                                <div 
                                                                    className="upload-thumb" 
                                                                    onClick={() => openImageViewer([{ label: 'Combined Report (Spectro + X-Ray)', url: spectrometerUrl, file: spectrometerFile }])} 
                                                                    title="Click to view full document"
                                                                >
                                                                    {isPdfFile(spectrometerFile, spectrometerUrl) ? (
                                                                        <div className="upload-thumb-pdf">
                                                                            <FileText size={22} className="pdf-icon" />
                                                                            <span className="upload-thumb-pdf-tag">PDF</span>
                                                                        </div>
                                                                    ) : (
                                                                        <img src={spectrometerUrl} alt="Combined Report" />
                                                                    )}
                                                                    <div className="thumb-hover-overlay"><ZoomIn size={16} /></div>
                                                                </div>
                                                                <div className="upload-file-meta">
                                                                    <p className="file-name" title={spectrometerFile.name}>{spectrometerFile.name}</p>
                                                                    <p className="file-subtext">
                                                                        {(isSpectrometerScanned && isXrayScanned) ? 'Both Spectrometer & X-Ray values extracted' : 'Extracts both Spectrometer & X-Ray readings from this document'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="upload-empty-state">
                                                                <UploadCloud size={30} className="upload-icon" />
                                                                <p className="upload-empty-title">Upload Combined Lab Report</p>
                                                                <p className="upload-empty-hint">1 document containing both Spectrometer and X-Ray results (PDF, PNG, JPG)</p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="upload-card-footer">
                                                        <input
                                                            type="file"
                                                            id="upload-0th-combined"
                                                            accept="image/*,application/pdf,.pdf"
                                                            style={{ display: 'none' }}
                                                            disabled={!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '')}
                                                            onChange={(e) => {
                                                                if (e.target.files[0]) {
                                                                    handleCombinedFileChange(e.target.files[0]);
                                                                    e.target.value = null;
                                                                }
                                                            }}
                                                        />
                                                        {isCombinedFile ? (
                                                            !hasScannedCurrentFiles && (
                                                                <div className="card-actions-row">
                                                                    <button
                                                                        type="button"
                                                                        className="btn-card-action btn-card-edit"
                                                                        onClick={(e) => { e.preventDefault(); openOcrModal(selectedItem, null, 'preview'); }}
                                                                    >
                                                                        Preview
                                                                    </button>
                                                                    <label
                                                                        htmlFor="upload-0th-combined"
                                                                        className="btn-card-action btn-card-change"
                                                                    >
                                                                        Change
                                                                    </label>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-card-action btn-card-remove"
                                                                        onClick={handleRemoveCombinedFile}
                                                                        title="Remove Combined Report"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            )
                                                        ) : (
                                                            <label
                                                                htmlFor={(!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '') || hasScannedCurrentFiles) ? undefined : "upload-0th-combined"}
                                                                className={`btn-primary btn-upload-card ${(!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '') || hasScannedCurrentFiles) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                <UploadCloud size={15} style={{ flexShrink: 0 }} />
                                                                <span>Upload Combined Report</span>
                                                            </label>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Card 1: Spectrometer Output (shown when 'spectrometer' OR ('both' && 'separate')) */}
                                            {(uploadReportMode === 'spectrometer' || (uploadReportMode === 'both' && bothUploadSubtype === 'separate')) && (
                                                <div className={`dual-upload-card ${spectrometerFile && !isCombinedFile ? 'has-file' : ''} ${(!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '')) ? 'disabled' : ''}`}>
                                                    <div className="upload-card-header">
                                                        <div className="upload-card-badge spec-badge">
                                                            <FileSpreadsheet size={15} />
                                                            <span>Spectrometer Output</span>
                                                        </div>
                                                        {isSpectrometerScanned && !isCombinedFile ? (
                                                            <span className="upload-status-chip">
                                                                <CheckCircle size={13} /> Scanned
                                                            </span>
                                                        ) : spectrometerFile && !isCombinedFile ? (
                                                            <span className="upload-status-chip" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #cbd5e1' }}>
                                                                <UploadCloud size={13} /> Uploaded
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    
                                                    <div className="upload-card-body">
                                                        {spectrometerFile && !isCombinedFile ? (
                                                            <div className="upload-preview-wrap">
                                                                <div 
                                                                    className="upload-thumb" 
                                                                    onClick={() => openImageViewer([{ label: 'Spectrometer Output', url: spectrometerUrl, file: spectrometerFile }])} 
                                                                    title="Click to view full document"
                                                                >
                                                                    {isPdfFile(spectrometerFile, spectrometerUrl) ? (
                                                                        <div className="upload-thumb-pdf">
                                                                            <FileText size={22} className="pdf-icon" />
                                                                            <span className="upload-thumb-pdf-tag">PDF</span>
                                                                        </div>
                                                                    ) : (
                                                                        <img src={spectrometerUrl} alt="Spectrometer Output" />
                                                                    )}
                                                                    <div className="thumb-hover-overlay"><ZoomIn size={16} /></div>
                                                                </div>
                                                                <div className="upload-file-meta">
                                                                    <p className="file-name" title={spectrometerFile.name}>{spectrometerFile.name}</p>
                                                                    <p className="file-subtext">{isSpectrometerScanned ? 'Composition values extracted' : 'Ready for scanning'}</p>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="upload-empty-state">
                                                                <UploadCloud size={30} className="upload-icon" />
                                                                <p className="upload-empty-title">Upload Spectrometer Report</p>
                                                                <p className="upload-empty-hint">PNG, JPG, or PDF scan format</p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="upload-card-footer">
                                                        <input
                                                            type="file"
                                                            id="upload-0th-spectrometer"
                                                            accept="image/*,application/pdf,.pdf"
                                                            style={{ display: 'none' }}
                                                            disabled={!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '')}
                                                            onChange={(e) => {
                                                                if (e.target.files[0]) {
                                                                    const file = e.target.files[0];
                                                                    setSpectrometerFile(file);
                                                                    setSpectrometerUrl(URL.createObjectURL(file));
                                                                    setIsSpectrometerScanned(false);
                                                                    e.target.value = null;
                                                                }
                                                            }}
                                                        />
                                                        {spectrometerFile && !isCombinedFile ? (
                                                            !hasScannedCurrentFiles && (
                                                                <div className="card-actions-row">
                                                                    <button
                                                                        type="button"
                                                                        className="btn-card-action btn-card-edit"
                                                                        onClick={(e) => { e.preventDefault(); openOcrModal(selectedItem, 'spectrometer', 'preview'); }}
                                                                    >
                                                                        Preview
                                                                    </button>
                                                                    <label
                                                                        htmlFor="upload-0th-spectrometer"
                                                                        className="btn-card-action btn-card-change"
                                                                    >
                                                                        Change
                                                                    </label>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-card-action btn-card-remove"
                                                                        onClick={() => {
                                                                            if (spectrometerUrl) URL.revokeObjectURL(spectrometerUrl);
                                                                            setSpectrometerFile(null);
                                                                            setSpectrometerUrl(null);
                                                                        }}
                                                                        title="Remove Spectrometer Image"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            )
                                                        ) : (
                                                            <label
                                                                htmlFor={(!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '') || hasScannedCurrentFiles) ? undefined : "upload-0th-spectrometer"}
                                                                className={`btn-primary btn-upload-card ${(!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '') || hasScannedCurrentFiles) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                <UploadCloud size={15} style={{ flexShrink: 0 }} />
                                                                <span>Upload Spectrometer</span>
                                                            </label>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Card 2: X-Ray Output (shown when 'xray' OR ('both' && 'separate')) */}
                                            {(uploadReportMode === 'xray' || (uploadReportMode === 'both' && bothUploadSubtype === 'separate')) && (
                                                <div className={`dual-upload-card ${xrayFile && !isCombinedFile ? 'has-file' : ''} ${(!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '')) ? 'disabled' : ''}`}>
                                                    <div className="upload-card-header">
                                                        <div className="upload-card-badge xray-badge">
                                                            <Zap size={15} />
                                                            <span>X-Ray Output</span>
                                                        </div>
                                                        {isXrayScanned && !isCombinedFile ? (
                                                            <span className="upload-status-chip">
                                                                <CheckCircle size={13} /> Scanned
                                                            </span>
                                                        ) : xrayFile && !isCombinedFile ? (
                                                            <span className="upload-status-chip" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #cbd5e1' }}>
                                                                <UploadCloud size={13} /> Uploaded
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    
                                                    <div className="upload-card-body">
                                                        {xrayFile && !isCombinedFile ? (
                                                            <div className="upload-preview-wrap">
                                                                <div 
                                                                    className="upload-thumb" 
                                                                    onClick={() => openImageViewer([{ label: 'X-Ray Output', url: xrayUrl, file: xrayFile }])} 
                                                                    title="Click to view full document"
                                                                >
                                                                    {isPdfFile(xrayFile, xrayUrl) ? (
                                                                        <div className="upload-thumb-pdf">
                                                                            <FileText size={22} className="pdf-icon" />
                                                                            <span className="upload-thumb-pdf-tag">PDF</span>
                                                                        </div>
                                                                    ) : (
                                                                        <img src={xrayUrl} alt="X-Ray Output" />
                                                                    )}
                                                                    <div className="thumb-hover-overlay"><ZoomIn size={16} /></div>
                                                                </div>
                                                                <div className="upload-file-meta">
                                                                    <p className="file-name" title={xrayFile.name}>{xrayFile.name}</p>
                                                                    <p className="file-subtext">{isXrayScanned ? 'Composition values extracted' : 'Ready for scanning'}</p>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="upload-empty-state">
                                                                <UploadCloud size={30} className="upload-icon" />
                                                                <p className="upload-empty-title">Upload X-Ray (XRF) Report</p>
                                                                <p className="upload-empty-hint">PNG, JPG, or PDF scan format</p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="upload-card-footer">
                                                        <input
                                                            type="file"
                                                            id="upload-0th-xray"
                                                            accept="image/*,application/pdf,.pdf"
                                                            style={{ display: 'none' }}
                                                            disabled={!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '')}
                                                            onChange={(e) => {
                                                                if (e.target.files[0]) {
                                                                    const file = e.target.files[0];
                                                                    setXrayFile(file);
                                                                    setXrayUrl(URL.createObjectURL(file));
                                                                    setIsXrayScanned(false);
                                                                    e.target.value = null;
                                                                }
                                                            }}
                                                        />
                                                        {xrayFile && !isCombinedFile ? (
                                                            !hasScannedCurrentFiles && (
                                                                <div className="card-actions-row">
                                                                    <button
                                                                        type="button"
                                                                        className="btn-card-action btn-card-edit"
                                                                        onClick={(e) => { e.preventDefault(); openOcrModal(selectedItem, 'xray', 'preview'); }}
                                                                    >
                                                                        Preview
                                                                    </button>
                                                                    <label
                                                                        htmlFor="upload-0th-xray"
                                                                        className="btn-card-action btn-card-change"
                                                                    >
                                                                        Change
                                                                    </label>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-card-action btn-card-remove"
                                                                        onClick={() => {
                                                                            if (xrayUrl) URL.revokeObjectURL(xrayUrl);
                                                                            setXrayFile(null);
                                                                            setXrayUrl(null);
                                                                        }}
                                                                        title="Remove X-Ray Image"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            )
                                                        ) : (
                                                            <label
                                                                htmlFor={(!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '') || hasScannedCurrentFiles) ? undefined : "upload-0th-xray"}
                                                                className={`btn-primary btn-upload-card ${(!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '') || hasScannedCurrentFiles) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                <UploadCloud size={15} style={{ flexShrink: 0 }} />
                                                                <span>Upload X-Ray</span>
                                                            </label>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {(spectrometerFile || xrayFile) && (
                                            <div className="mt-4 mb-4 text-center">
                                                {hasScannedCurrentFiles ? (
                                                    <div className="flex items-center justify-center gap-4" style={{ padding: '10px 20px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', width: 'fit-content', margin: '0 auto' }}>
                                                        <span className="text-emerald-600 font-bold flex items-center text-sm">
                                                            <CheckCircle size={16} className="mr-2" /> Reports Scanned
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="btn-secondary flex items-center"
                                                            style={{ padding: '6px 12px', fontSize: '13px' }}
                                                            onClick={() => {
                                                                setIsSpectrometerScanned(false);
                                                                setIsXrayScanned(false);
                                                            }}
                                                        >
                                                            <Settings size={14} className="mr-1" /> Edit Uploads
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn-primary"
                                                        onClick={() => openOcrModal(selectedItem)}
                                                        style={{ padding: '10px 20px', fontSize: '14px', margin: '0 auto', display: 'inline-flex' }}
                                                    >
                                                        <Scan size={16} className="mr-2" /> Scan Uploaded Reports
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="calculate-action-wrapper">
                                        <button
                                            className={`btn-primary w-full flex justify-center py-3 text-base ${(!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '') || !Object.values(currentInputs).some(val => val !== '') || isAnalyzing || hasPendingScans) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            onClick={processIteration}
                                            disabled={!selectedItem || !baseWeight || (maxChargeWeightLbs === '' && maxChargeWeightPercent === '') || !Object.values(currentInputs).some(val => val !== '') || isAnalyzing || hasPendingScans}
                                        >
                                            {isAnalyzing ? <><RefreshCw className="spin mr-2" /> Executing Computation...</> : <><Settings className="mr-2" /> {userModule === 'FE' ? 'Calculate Additives' : 'Calculate Interdependencies'}</>}
                                        </button>
                                    </div>
                                </motion.div>
                            )
                        ) : activeTransaction ? (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="workspace-inner active-batch flex flex-col">
                                <div className="flex justify-between items-start mb-4 flex-shrink-0">
                                    <div>
                                        <h2 className="text-primary m-0 mb-1 text-2xl font-bold">Batch {activeTransaction.id}</h2>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-secondary text-sm" style={{ alignItems: 'center' }}>
                                            <span>Target: <strong className="text-slate-700">{activeTransaction.itemName}</strong></span>
                                            <span style={{ margin: '0 2px', opacity: 0.4 }}>•</span>
                                            <span>Initial Weight: <strong className="text-slate-700">{activeTransaction.initialWeight?.toLocaleString()} lbs</strong></span>
                                            {activeTransaction.maxChargeWeight && (
                                                <>
                                                    <span style={{ margin: '0 2px', opacity: 0.4 }}>•</span>
                                                    <span>Max Limit: <strong className="text-slate-700">{activeTransaction.maxChargeWeight?.toLocaleString()} lbs</strong></span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className={`px-4 py-1.5 rounded-full font-bold text-sm flex items-center gap-1.5 ${activeTransaction.status === 'In Spec' ? 'bg-sky-100 text-sky-700'
                                            : activeTransaction.status === 'Poured' ? 'bg-sky-100 text-sky-700'
                                                : activeTransaction.status === 'Expired' ? 'bg-sky-100 text-sky-700'
                                                    : activeTransaction.status === 'Manually Closed' ? 'bg-slate-200 text-slate-600'
                                                        : 'bg-sky-100 text-sky-700'
                                            }`}>
                                            {activeTransaction.status === 'Poured' ? (
                                                <><CheckCircle size={14} /> Poured</>
                                            ) : activeTransaction.status === 'Expired' ? (
                                                <><AlertTriangle size={14} /> Expired</>
                                            ) : activeTransaction.status}
                                        </div>
                                        {activeTransaction.status !== 'In Spec' && activeTransaction.status !== 'Manually Closed' && activeTransaction.status !== 'Poured' && activeTransaction.status !== 'Expired' && (
                                            <button
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border border-sky-200 text-sky-700 bg-sky-50 hover:bg-sky-100 transition-colors"
                                                onClick={() => setShowCloseBatchModal(true)}
                                            >
                                                <X size={14} /> Close Batch
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {activeTransaction.status === 'Poured' && (
                                    <div className="flex-shrink-0 mb-4 bg-sky-50 border border-sky-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
                                        <div>
                                            <p className="m-0 text-xs font-bold text-sky-700 uppercase tracking-wide mb-1 flex items-center">
                                                <CheckCircle size={16} className="mr-1.5 text-sky-600" /> Batch Successfully Poured Out
                                            </p>
                                            <p className="m-0 text-sm text-slate-700">
                                                Furnace batch poured at <span className="font-semibold text-slate-900">{activeTransaction.pouredAt}</span> by <span className="font-semibold text-slate-900">{activeTransaction.pouredBy || 'Operator'}</span>.
                                            </p>
                                        </div>
                                        <span className="px-3 py-1 bg-sky-200 text-sky-800 text-xs font-extrabold rounded-lg uppercase tracking-wider">
                                            Poured Out
                                        </span>
                                    </div>
                                )}

                                {activeTransaction.status === 'Expired' && (
                                    <div className="flex-shrink-0 mb-4 bg-sky-50 border border-sky-200 rounded-xl p-4 shadow-sm">
                                        <p className="m-0 text-xs font-bold text-sky-700 uppercase tracking-wide mb-1 flex items-center">
                                            <AlertTriangle size={16} className="mr-1.5 text-sky-600" /> Batch Expired — Pour Window Elapsed
                                        </p>
                                        <p className="m-0 text-sm text-slate-700">
                                            The pour window has passed ({activeTransaction.expiredAt || 'Expired'}).
                                        </p>
                                    </div>
                                )}

                                {activeTransaction.status === 'Manually Closed' && (
                                    <div className="flex-shrink-0 mb-4 bg-slate-50 border border-red-100 rounded-xl p-4">
                                        <p className="m-0 text-xs font-semibold text-red-400 uppercase tracking-wide mb-1">Batch Manually Closed</p>
                                        <p className="m-0 text-sm text-slate-700">
                                            <span className="font-medium">{activeTransaction.closedAt}</span> by <span className="font-semibold text-slate-800">{activeTransaction.closedBy}</span>
                                        </p>
                                        {activeTransaction.closureRemarks && (
                                            <p className="m-0 mt-2 text-sm text-slate-600 italic border-t border-slate-200 pt-2">"{activeTransaction.closureRemarks}"</p>
                                        )}
                                    </div>
                                )}

                                {/* Iron (FE) Module Specific Pour Notification & Timer Banner */}
                                {userModule === 'FE' && (activeTransaction.status === 'In Spec' || (pourTimerMap[activeTransaction.id] && pourTimerMap[activeTransaction.id].status === 'running')) && activeTransaction.status !== 'Poured' && activeTransaction.status !== 'Expired' && activeTransaction.status !== 'Manually Closed' && (
                                    <div className="flex-shrink-0">
                                        {(!pourTimerMap[activeTransaction.id] || pourTimerMap[activeTransaction.id].status === 'idle') ? (
                                            /* TOASTER / PROMPT BANNER */
                                            <div className="pour-banner-prompt">
                                                <div className="pour-banner-title">
                                                    <Flame size={20} color="#0ea5e9" />
                                                    <span>Iron Batch In Spec — Pour Needed</span>
                                                </div>
                                                <button
                                                    onClick={() => handleStartPourTimer(activeTransaction.id)}
                                                    className="btn-start-pour"
                                                >
                                                    <Clock size={16} /> Start Pour Timer
                                                </button>
                                            </div>
                                        ) : pourTimerMap[activeTransaction.id]?.status === 'running' ? (
                                            /* ACTIVE COUNTDOWN TIMER CARD (Primary Blue Theme) */
                                            <div className="pour-timer-card">
                                                <div className="pour-timer-label">
                                                    <Clock size={18} color="#0ea5e9" />
                                                    <span>Pour Timer</span>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <span className="pour-timer-clock">
                                                        {formatRemainingTime(getRemainingSeconds(activeTransaction.id))}
                                                    </span>
                                                    <button
                                                        onClick={() => handleMarkBatchPoured(activeTransaction.id)}
                                                        className="btn-mark-poured"
                                                    >
                                                        <CheckCircle size={16} /> Mark Batch Poured
                                                    </button>
                                                </div>
                                            </div>
                                        ) : null}
                                    </div>
                                )}

                                {/* Prediction Matrix Spreadsheet View */}
                                {/* <div className="flex-shrink-0 bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 overflow-x-auto w-full">
                                    <div className="flex justify-between items-center mb-3">
                                        <h4 className="m-0 text-slate-800 flex items-center font-bold">
                                            <Box className="mr-2 text-indigo-500" size={18} /> Active Adjustment Matrix
                                        </h4>
                                    </div>
                                    <div className="overflow-x-auto" style={{ maxWidth: '100%' }}>
                                        <table className="w-full text-xs border-collapse border border-slate-300 min-w-max">
                                            <thead>
                                                <tr className="bg-slate-100 text-slate-700">
                                                    <th className="p-2 border border-slate-300 font-bold" colSpan={3}>Target Specifications:</th>
                                                    {items.find(i => i.id === activeTransaction.itemSpecId)?.compounds.map(comp => {
                                                        const elName = comp.element.match(/\(([A-Za-z0-9,]+)\)/) ? comp.element.match(/\(([A-Za-z0-9,]+)\)/)[1] : comp.element.split(' ')[0];
                                                        return <th key={`head-${comp.element}`} className="p-2 border border-slate-300 font-bold text-center w-16 bg-blue-50/50">{elName}</th>;
                                                    })}
                                                </tr>
                                                <tr className="bg-white text-slate-500">
                                                    <th className="p-2 border border-slate-300 font-medium">Step</th>
                                                    <th className="p-2 border border-slate-300 font-medium">Weight (lbs)</th>
                                                    <th className="p-2 border border-slate-300 font-medium">Material / Lot</th>
                                                    {items.find(i => i.id === activeTransaction.itemSpecId)?.compounds.map(comp => (
                                                        <th key={`exp-${comp.element}`} className="p-2 border border-slate-300 text-center text-[10px] break-words whitespace-normal leading-tight h-8 font-mono">
                                                            {comp.min}% - {comp.max}%
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                              
                                                <tr>
                                                    <td className="p-2 border border-slate-300 text-center font-bold">1</td>
                                                    <td className="p-2 border border-slate-300 text-right font-mono font-bold bg-slate-50">{activeTransaction.initialWeight}</td>
                                                    <td className="p-2 border border-slate-300 text-slate-600 font-medium">BASE CHARGE</td>
                                                    {activeTransaction.rounds[0].data.map(comp => (
                                                        <td key={`base-${comp.element}`} className="p-2 border border-slate-300 text-center font-mono">
                                                            {((comp.actual / 100) * activeTransaction.initialWeight).toFixed(1)}
                                                        </td>
                                                    ))}
                                                </tr>

                                                {activeTransaction.rounds[activeTransaction.rounds.length - 1].data.map((res, i) => {
                                                    if (res.status === 'In Spec' || parseFloat(res.adjWeightLbs) === 0) return null;
                                                    const latestRound = activeTransaction.rounds[activeTransaction.rounds.length - 1];
                                                    return (
                                                        <tr key={`add-${i}`} className="bg-amber-50/30">
                                                            <td className="p-2 border border-slate-300 text-center text-slate-500">{activeTransaction.rounds.length + 1}.{i + 1}</td>
                                                            <td className="p-2 border border-slate-300 text-right font-mono font-bold text-amber-700 bg-amber-50">{res.adjWeightLbs}</td>
                                                            <td className="p-2 border border-slate-300 text-slate-600 truncate max-w-[150px]" title={res.adjString}>{res.adjString}</td>
                                                            {latestRound.data.map(comp => {
                                                                const matchCode = comp.element.match(/\(([A-Za-z]+).*?\)/);
                                                                const code = matchCode ? matchCode[1] : comp.element.split(' ')[0];
                                                                // check if this cell is receiving the addition
                                                                // the engine sets targetElementToAdd in interdependencyEngine, but we don't have that explicitly mapped in the object returning context.
                                                                // But we know 'adjString' and 'code'.
                                                                const isSelf = code === res.targetElementToAdd || (code === 'Cu' && res.adjString.includes('Cu')); // simplified check based on string.
                                                                // Better: use the code mapped in engine. The engine uses code directly. If 'res.status === Too Low' it adds 'code'. If 'Too High', it dilutes with Cu.
                                                                let match = '0';
                                                                if (res.status === 'Too Low' && comp.element === res.element) match = res.adjWeightLbs;
                                                                else if (res.status === 'Too High' && (code === 'Cu' || code === 'Copper')) match = res.adjWeightLbs; // Dilution goes to Cu normally

                                                                return (
                                                                    <td key={`add-${i}-${comp.element}`} className={`p-2 border border-slate-300 text-center font-mono ${match !== '0' ? 'text-amber-700 font-bold' : 'text-slate-300'}`}>
                                                                        {match}
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                    );
                                                })}

                                              
                                                <tr className="bg-slate-100 font-bold border-t-2 border-slate-400">
                                                    <td className="p-2 border border-slate-300">Total/Net</td>
                                                    <td className="p-2 border border-slate-300 text-right font-mono text-indigo-700 bg-indigo-50">{activeTransaction.baseWeight}</td>
                                                    <td className="p-2 border border-slate-300 text-right text-slate-600">ELEMENTAL TOTALS &rarr;</td>
                                                    {activeTransaction.rounds[activeTransaction.rounds.length - 1].data.map(comp => {
                                                        // total is finalMass calculated internally by engine -> (predictedPercent/100)*predictedWeight
                                                        const latestRound = activeTransaction.rounds[activeTransaction.rounds.length - 1];
                                                        const finalMass = (comp.predictedPercent / 100) * latestRound.predictedWeight;
                                                        return (
                                                            <td key={`tot-${comp.element}`} className="p-2 border border-slate-300 text-center font-mono bg-white text-indigo-800">
                                                                {finalMass.toFixed(1)}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>

                                             
                                                <tr className="bg-emerald-50 text-emerald-800 font-bold">
                                                    <td className="p-2 border border-slate-300" colSpan={3} style={{ textAlign: 'right' }}>FINAL PREDICTED %</td>
                                                    {activeTransaction.rounds[activeTransaction.rounds.length - 1].data.map(comp => (
                                                        <td key={`pct-${comp.element}`} className={`p-2 border border-slate-300 text-center font-mono ${comp.status === 'In Spec' ? 'text-emerald-600' : 'text-amber-600'}`}>
                                                            {comp.predictedPercent}%
                                                        </td>
                                                    ))}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div> */}

                                {/* Rounds Content — latest round first, accordion collapse */}
                                <div className="rounds-history flex flex-col gap-4 mt-6 mb-4">
                                    {[...activeTransaction.rounds].reverse().map((round, idx) => {
                                        const isLatest = idx === 0;
                                        const isCollapsed = collapsedRounds[round.id] ?? !isLatest;
                                        return (
                                            <div key={round.id} className={`round-card-wrapper ${isLatest ? 'is-latest-round' : ''}`}>
                                                {/* Accordion Header */}
                                                <button
                                                    type="button"
                                                    onClick={() => toggleRoundCollapse(round.id)}
                                                    className={`round-accordion-btn ${!isCollapsed ? 'is-open' : ''}`}
                                                >
                                                    {/* Left: Round Title, Status, and Timestamp */}
                                                    <div className="round-header-left">
                                                        <div className="round-title-group">
                                                            <div className="round-title-row">
                                                                <h4 className="round-title-text">
                                                                    Round {round.roundNumber}
                                                                </h4>
                                                                <span className={`round-status-pill ${round.overallStatus === 'In Spec' ? 'round-status-in-spec' : 'round-status-action-needed'}`}>
                                                                    {round.overallStatus === 'In Spec' ? '✓ In Spec' : 'Action Needed'}
                                                                </span>
                                                            </div>
                                                            <p className="round-meta-text">
                                                                {round.createdAt && <span>{round.createdAt}</span>}
                                                                {round.createdAt && round.createdBy && <span>•</span>}
                                                                {round.createdBy && <span>by {round.createdBy}</span>}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {/* Right: Metrics Pills, Actions & Chevron */}
                                                    <div className="round-header-right">
                                                        {!isCollapsed && (
                                                            <div className="round-metrics-bar">
                                                                <span className="round-pill round-pill-neutral">
                                                                    Start: <strong>{round.baseWeight?.toLocaleString()} lbs</strong>
                                                                </span>
                                                                {activeTransaction.maxChargeWeight && (
                                                                    <span className="round-pill round-pill-neutral">
                                                                        Max: <strong>{activeTransaction.maxChargeWeight?.toLocaleString()} lbs</strong>
                                                                    </span>
                                                                )}
                                                                {((round.totalNewWeightAdded || 0) + parseInt(round.pCu || 0, 10)) > 0 && (
                                                                    <span className="round-pill round-pill-added">
                                                                        +{((round.totalNewWeightAdded || 0) + parseInt(round.pCu || 0, 10)).toLocaleString(undefined, { maximumFractionDigits: 2 })} lbs
                                                                    </span>
                                                                )}
                                                                <span className="round-pill round-pill-est">
                                                                    Est: <strong>{((round.baseWeight || 0) + (round.totalNewWeightAdded || 0) + parseInt(round.pCu || 0, 10)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lbs</strong>
                                                                </span>
                                                            </div>
                                                        )}

                                                        <div className="round-actions-wrap">
                                                            {(() => {
                                                                const reportImages = [];
                                                                if (round.spectrometerImage && round.xrayImage && round.spectrometerImage === round.xrayImage) {
                                                                    reportImages.push({
                                                                        label: 'Combined Report (Spectro + X-Ray)',
                                                                        url: round.spectrometerImage.replace('storage.googleapis.com/download/storage/v1/b/', 'firebasestorage.googleapis.com/v0/b/')
                                                                    });
                                                                } else {
                                                                    if (round.spectrometerImage) {
                                                                        reportImages.push({
                                                                            label: 'Spectrometer Output',
                                                                            url: round.spectrometerImage.replace('storage.googleapis.com/download/storage/v1/b/', 'firebasestorage.googleapis.com/v0/b/')
                                                                        });
                                                                    }
                                                                    if (round.xrayImage) {
                                                                        reportImages.push({
                                                                            label: 'X-Ray Output',
                                                                            url: round.xrayImage.replace('storage.googleapis.com/download/storage/v1/b/', 'firebasestorage.googleapis.com/v0/b/')
                                                                        });
                                                                    }
                                                                }
                                                                if (reportImages.length === 0) return null;

                                                                if (reportImages.length === 1) {
                                                                    const isSinglePdf = isPdfFile(null, reportImages[0].url);
                                                                    const isComb = reportImages[0].label.includes('Combined');
                                                                    return (
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => { 
                                                                                e.stopPropagation(); 
                                                                                openImageViewer(reportImages, 0); 
                                                                            }}
                                                                            className="btn-round-action btn-round-report"
                                                                            title={`View ${reportImages[0].label}`}
                                                                        >
                                                                            {isSinglePdf ? <FileText size={14} /> : (isComb ? <Layers size={14} /> : <Image size={14} />)} {isComb ? 'View Combined Report' : reportImages[0].label.includes('X-Ray') ? 'View X-Ray' : reportImages[0].label.includes('Spectrometer') ? 'View Spectrometer' : 'View Report'}
                                                                        </button>
                                                                    );
                                                                }

                                                                return (
                                                                    <div className="flex items-center" style={{ gap: '8px' }} onClick={(e) => e.stopPropagation()}>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openImageViewer(reportImages, 0)}
                                                                            className="btn-round-action btn-round-report"
                                                                            title="View Spectrometer Report"
                                                                        >
                                                                            {isPdfFile(null, reportImages[0].url) ? <FileText size={13} /> : <FileSpreadsheet size={13} />} Spectrometer
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openImageViewer(reportImages, 1)}
                                                                            className="btn-round-action btn-round-report btn-round-xray"
                                                                            title="View X-Ray Report"
                                                                        >
                                                                            {isPdfFile(null, reportImages[1].url) ? <FileText size={13} /> : <Zap size={13} />} X-Ray
                                                                        </button>
                                                                    </div>
                                                                );
                                                            })()}

                                                            {round.data && round.data.length > 0 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => { e.stopPropagation(); setChargeModal({ round }); setChargeModalSearch(''); }}
                                                                    title="View charge addition summary"
                                                                    className="btn-round-action btn-round-summary"
                                                                >
                                                                    <FileText size={13} />
                                                                    Add Summary
                                                                </button>
                                                            )}

                                                            <div className="round-chevron-wrap">
                                                                <ChevronDown
                                                                    size={18}
                                                                    style={{
                                                                        transition: 'transform 0.2s ease',
                                                                        transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </button>

                                                {/* Accordion Body */}
                                                {!isCollapsed && (
                                                    <div style={{ padding: '0 20px 16px 20px' }}>
                                                        {round.cuAdjustment && (
                                                            <div style={{ padding: '9px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '12px', color: '#1e3a8a', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                                                <span>
                                                                    <strong>Copper Exception Applied:</strong> Lab Actual: <strong>{round.cuAdjustment.actualCu}%</strong> | Computed (100 − Σ others): <strong>{round.cuAdjustment.computedCu}%</strong> | Spec Min: <strong>{round.cuAdjustment.cuMin}%</strong>
                                                                </span>
                                                                <span style={{ fontWeight: 600, color: '#1d4ed8', background: '#ffffff', padding: '2px 8px', borderRadius: '4px', border: '1px solid #dbeafe' }}>
                                                                    {round.cuAdjustment.isComputedChosen ? `Using Computed: ${round.cuAdjustment.chosenCu}% (Nearest to Min)` : `Using Actual: ${round.cuAdjustment.chosenCu}% (Nearest to Min)`}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <div className="overflow-x-auto">
                                                            <table className="round-table w-full text-left text-sm">
                                                                <thead>
                                                                    <tr className="border-b border-slate-100">
                                                                        <th className="py-2 text-slate-500 font-medium">Compound</th>
                                                                        <th className="py-2 text-slate-500 font-medium">Spec Range</th>
                                                                        <th className="py-2 text-slate-500 font-medium">Actual</th>
                                                                        <th className="py-2 text-slate-500 font-medium">Variance</th>
                                                                        <th className="py-2 text-slate-500 font-medium">Adjustment Action</th>
                                                                        <th className="py-2 text-slate-500 font-medium text-center">Loss Factor</th>
                                                                        <th className="py-2 text-slate-500 font-medium text-center">Loss Comp. (lbs)</th>
                                                                        <th className="py-2 text-slate-500 font-medium text-right">Predicted %</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {round.data.map((row, i) => (
                                                                        <tr key={i} className={`border-b border-slate-50 last:border-0 ${row.status !== 'In Spec' ? 'bg-amber-50/30' : ''}`}>
                                                                            <td className="py-3 font-semibold text-slate-700">{row.element}</td>
                                                                            <td className="py-3 text-slate-500 font-mono text-xs">
                                                                                {isCopperElement(row.element) && (!row.max || Number(row.max) <= 0)
                                                                                    ? `≥ ${row.min}% (No Max)`
                                                                                    : `${row.min}% - ${row.max}%`}
                                                                            </td>
                                                                            <td className="py-3 font-bold text-slate-800 font-mono">{row.actual}%</td>

                                                                            <td className="py-3">
                                                                                {row.status === 'In Spec' ? (
                                                                                    <span className="text-emerald-600 flex items-center text-xs font-bold leading-none mt-1"><CheckCircle className="mr-1" size={14} /> Valid</span>
                                                                                ) : row.status === 'Too Low' ? (
                                                                                    <span className="text-amber-600 flex items-center text-xs font-bold leading-none mt-1"><AlertTriangle className="mr-1" size={14} /> {row.variance.toFixed(4)}%</span>
                                                                                ) : (
                                                                                    <span className="text-red-500 flex items-center text-xs font-bold leading-none mt-1"><AlertCircle className="mr-1" size={14} /> +{row.variance.toFixed(3)}%</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="py-3">
                                                                                {(() => {
                                                                                    // if (row.status === 'In Spec') return <span className="text-slate-300 text-xs">--</span>;
                                                                                    // Try adjString first, then fall back to round.data

                                                                                    let adjText = '';
                                                                                    if (!adjText && round.data && round.data.length > 0) {
                                                                                        const match = round.data.find(a =>
                                                                                            a.element === row.element ||
                                                                                            row.element.includes(a.element) ||
                                                                                            (a.element && a.element.includes(row.element))
                                                                                        );
                                                                                        if (match) {

                                                                                            if (match.adjString?.includes("⚠ Manual intervention required")) {
                                                                                                adjText = `⚠ Manual intervention required`;
                                                                                            }
                                                                                            else if (match.adjWeightLbs === 0 || match.lossCompLbs != 0) {
                                                                                                adjText = `--`;
                                                                                            }
                                                                                            else {
                                                                                                adjText = `Add ${match.adjWeightLbs?.toLocaleString()} lbs`;
                                                                                            }
                                                                                            //adjText = match.adjString;
                                                                                        }
                                                                                    }
                                                                                    if (!adjText || adjText === `--`) return <span className="text-slate-300 text-xs">--</span>;
                                                                                    return (
                                                                                        <div className={`inline-block bg-amber-100 text-amber-800 } px-3 py-1 rounded font-bold text-xs max-w-[220px] text-left leading-tight`}>
                                                                                            {adjText}
                                                                                        </div>
                                                                                    );
                                                                                })()}
                                                                            </td>
                                                                            <td className="py-3 text-center">

                                                                                {row.loss_factor > 0 ? (
                                                                                    <span className="inline-block bg-orange-50 text-orange-700 border border-orange-200 rounded px-2 py-0.5 text-xs font-bold font-mono">{row.loss_factor}%</span>
                                                                                ) : (
                                                                                    <span className="text-slate-300 text-xs">—</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="py-3 text-center">
                                                                                {row.lossCompLbs > 0 ? (
                                                                                    <span className="inline-block bg-orange-50 text-orange-700 border border-orange-200 rounded px-2 py-0.5 text-xs font-bold font-mono">{row.lossCompLbs?.toLocaleString()} lbs</span>
                                                                                ) : (
                                                                                    <span className="text-slate-300 text-xs">—</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="py-3 font-bold text-indigo-600 font-mono text-right"><span className="text-[10px] text-indigo-300 mr-1">est.</span>{row.predictedPercent}%</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        {/* Adjustment Plan — ordered step-by-step action list */}
                                                        {/* {round.additions && round.additions.length > 0 && (
                                                            <div style={{ marginTop: '18px', borderTop: '1px solid #e2e8f0', paddingTop: '14px' }}>
                                                                <p style={{ margin: '0 0 10px 0', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                    <Scale size={13} style={{ color: '#6366f1' }} />
                                                                    Adjustment Plan — Execute in Order
                                                                </p>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                    {round.additions.map((add, ai) => (
                                                                        <div
                                                                            key={ai}
                                                                            style={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '10px',
                                                                                padding: '8px 12px',
                                                                                borderRadius: '8px',
                                                                                background: add.action === 'Add' ? 'rgba(16,185,129,0.06)' : add.action === 'LossComp' ? 'rgba(245,158,11,0.07)' : 'rgba(239,68,68,0.06)',
                                                                                border: add.action === 'Add' ? '1px solid rgba(16,185,129,0.2)' : add.action === 'LossComp' ? '1px solid rgba(245,158,11,0.3)' : '1px solid rgba(239,68,68,0.2)',
                                                                            }}
                                                                        >
                                                                            <span style={{
                                                                                flexShrink: 0,
                                                                                width: '22px', height: '22px',
                                                                                borderRadius: '50%',
                                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                fontSize: '10px', fontWeight: 800,
                                                                                background: add.action === 'Add' ? '#10b981' : add.action === 'LossComp' ? '#f59e0b' : '#ef4444',
                                                                                color: '#fff',
                                                                            }}>
                                                                                {add.step}
                                                                            </span>
                                                                            <span style={{ fontSize: '12px', fontWeight: 700, color: add.action === 'Add' ? '#065f46' : add.action === 'LossComp' ? '#92400e' : '#991b1b', minWidth: '60px' }}>
                                                                                {add.action === 'Add' ? '▲ ADD' : add.action === 'LossComp' ? '◆ LOSS COMP' : '▼ CUT'}
                                                                            </span>
                                                                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', flex: 1 }}>
                                                                                {add.action === 'Add'
                                                                                    ? <><strong>{add.element}</strong> — charge <strong style={{ fontFamily: 'monospace' }}>{add.lbs.toLocaleString()} lbs</strong></>
                                                                                    : add.action === 'LossComp'
                                                                                        ? <><strong>{add.element}</strong> — heat-loss comp. <strong style={{ fontFamily: 'monospace' }}>{add.lbs.toLocaleString()} lbs</strong> <span style={{ fontSize: '10px', color: '#b45309' }}>({add.lossFactor}% loss)</span></>
                                                                                        : <>Cut <strong>{add.element}</strong> → add <strong>{add.diluent}</strong> <strong style={{ fontFamily: 'monospace' }}>{add.lbs.toLocaleString()} lbs</strong></>
                                                                                }
                                                                            </span>
                                                                            {add.action !== 'LossComp' && add.lossFactor > 0 && (
                                                                                <span style={{ fontSize: '10px', color: '#94a3b8', fontStyle: 'italic', flexShrink: 0 }}>
                                                                                    loss {add.lossFactor}%
                                                                                </span>
                                                                            )}
                                                                            {add.action !== 'LossComp' && round.snapshots && (() => {
                                                                                const snap = round.snapshots.find(s => s.step === add.step && s.action === add.action);
                                                                                return snap ? (
                                                                                    <button
                                                                                        onClick={(e) => { e.stopPropagation(); setSnapshotModal({ snapshot: snap, roundNumber: round.roundNumber, allSnapshots: round.snapshots }); }}
                                                                                        title="View melt state after this step"
                                                                                        style={{
                                                                                            flexShrink: 0,
                                                                                            width: '24px', height: '24px',
                                                                                            borderRadius: '50%',
                                                                                            border: '1.5px solid #818cf8',
                                                                                            background: 'rgba(99,102,241,0.08)',
                                                                                            color: '#6366f1',
                                                                                            cursor: 'pointer',
                                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                            padding: 0,
                                                                                            transition: 'all 0.15s ease',
                                                                                        }}
                                                                                        onMouseEnter={e => { e.currentTarget.style.background = '#6366f1'; e.currentTarget.style.color = '#fff'; }}
                                                                                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; e.currentTarget.style.color = '#6366f1'; }}
                                                                                    >
                                                                                        <Info size={13} />
                                                                                    </button>
                                                                                ) : null;
                                                                            })()}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )} */}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>

                                {/* Add New Round Tool */}
                                {activeTransaction.status !== 'In Spec' && activeTransaction.status !== 'Manually Closed' && (
                                    <div className="new-round-box mt-2 bg-slate-50 border border-slate-200 rounded-xl p-5 mb-2">
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <h4 className="text-slate-800 m-0 mb-1 flex items-center font-bold">
                                                    <Beaker size={18} className="mr-2 text-indigo-500" /> Initiate Round {activeTransaction.rounds.length + 1}
                                                </h4>
                                                <p className="text-slate-500 text-xs m-0">
                                                    Upload latest Spectrometer output, X-Ray output, or both to review and calculate adjustments.
                                                </p>
                                            </div>
                                        </div>

                                        {/* Report Upload Mode Selector */}
                                        <div className="report-mode-toolbar mb-3">
                                            <div className="report-mode-tabs">
                                                <button
                                                    type="button"
                                                    className={`report-mode-tab ${roundUploadReportMode === 'both' ? 'active' : ''}`}
                                                    onClick={() => handleSwitchReportMode('both', true)}
                                                >
                                                    <Layers size={14} />
                                                    <span>Both (Spectro + X-Ray)</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`report-mode-tab ${roundUploadReportMode === 'spectrometer' ? 'active' : ''}`}
                                                    onClick={() => handleSwitchReportMode('spectrometer', true)}
                                                >
                                                    <FileSpreadsheet size={14} />
                                                    <span>Only Spectro</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`report-mode-tab ${roundUploadReportMode === 'xray' ? 'active' : ''}`}
                                                    onClick={() => handleSwitchReportMode('xray', true)}
                                                >
                                                    <Zap size={14} />
                                                    <span>Only X-Ray</span>
                                                </button>
                                            </div>

                                            {roundUploadReportMode === 'both' && (
                                                <div className="report-subtype-switch">
                                                    <button
                                                        type="button"
                                                        className={`report-subtype-btn ${roundBothUploadSubtype === 'combined' ? 'active' : ''}`}
                                                        onClick={() => setRoundBothUploadSubtype('combined')}
                                                    >
                                                        1 Combined File
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`report-subtype-btn ${roundBothUploadSubtype === 'separate' ? 'active' : ''}`}
                                                        onClick={() => setRoundBothUploadSubtype('separate')}
                                                    >
                                                        2 Separate Files
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="dual-upload-grid">
                                            {/* Combined Report Card (when Both -> 1 Combined File is chosen) */}
                                            {roundUploadReportMode === 'both' && roundBothUploadSubtype === 'combined' && (
                                                <div className={`dual-upload-card combined-card ${isCombinedFile ? 'has-file' : ''}`}>
                                                    <div className="upload-card-header">
                                                        <div className="upload-card-badge combined-badge">
                                                            <Layers size={15} />
                                                            <span>Combined Report (Spectro + X-Ray)</span>
                                                        </div>
                                                        {(isSpectrometerScanned && isXrayScanned) ? (
                                                            <span className="upload-status-chip">
                                                                <CheckCircle size={13} /> Scanned
                                                            </span>
                                                        ) : isCombinedFile ? (
                                                            <span className="upload-status-chip" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #cbd5e1' }}>
                                                                <UploadCloud size={13} /> Uploaded
                                                            </span>
                                                        ) : null}
                                                    </div>

                                                    <div className="upload-card-body">
                                                        {isCombinedFile ? (
                                                            <div className="upload-preview-wrap">
                                                                <div 
                                                                    className="upload-thumb" 
                                                                    onClick={() => openImageViewer([{ label: 'Combined Report (Spectro + X-Ray)', url: spectrometerUrl, file: spectrometerFile }])} 
                                                                    title="Click to view full document"
                                                                >
                                                                    {isPdfFile(spectrometerFile, spectrometerUrl) ? (
                                                                        <div className="upload-thumb-pdf">
                                                                            <FileText size={22} className="pdf-icon" />
                                                                            <span className="upload-thumb-pdf-tag">PDF</span>
                                                                        </div>
                                                                    ) : (
                                                                        <img src={spectrometerUrl} alt="Combined Report" />
                                                                    )}
                                                                    <div className="thumb-hover-overlay"><ZoomIn size={16} /></div>
                                                                </div>
                                                                <div className="upload-file-meta">
                                                                    <p className="file-name" title={spectrometerFile.name}>{spectrometerFile.name}</p>
                                                                    <p className="file-subtext">
                                                                        {(isSpectrometerScanned && isXrayScanned) ? 'Both Spectrometer & X-Ray values extracted' : 'Extracts both Spectrometer & X-Ray readings from this document'}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="upload-empty-state">
                                                                <UploadCloud size={30} className="upload-icon" />
                                                                <p className="upload-empty-title">Upload Combined Lab Report</p>
                                                                <p className="upload-empty-hint">1 document containing both Spectrometer and X-Ray results (PDF, PNG, JPG)</p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="upload-card-footer">
                                                        <input
                                                            type="file"
                                                            id="upload-round-combined"
                                                            accept="image/*,application/pdf,.pdf"
                                                            style={{ display: 'none' }}
                                                            onChange={(e) => {
                                                                if (e.target.files[0]) {
                                                                    handleCombinedFileChange(e.target.files[0]);
                                                                    e.target.value = null;
                                                                }
                                                            }}
                                                        />
                                                        {isCombinedFile ? (
                                                            !hasScannedCurrentFiles && (
                                                                <div className="card-actions-row">
                                                                    <button
                                                                        type="button"
                                                                        className="btn-card-action btn-card-edit"
                                                                        onClick={(e) => { e.preventDefault(); openOcrModal(activeTransaction.itemSpecId, null, 'preview'); }}
                                                                    >
                                                                        Preview
                                                                    </button>
                                                                    <label
                                                                        htmlFor="upload-round-combined"
                                                                        className="btn-card-action btn-card-change"
                                                                    >
                                                                        Change
                                                                    </label>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-card-action btn-card-remove"
                                                                        onClick={handleRemoveCombinedFile}
                                                                        title="Remove Combined Report"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            )
                                                        ) : (
                                                            <label
                                                                htmlFor={hasScannedCurrentFiles ? undefined : "upload-round-combined"}
                                                                className={`btn-primary btn-upload-card ${hasScannedCurrentFiles ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                <UploadCloud size={15} style={{ flexShrink: 0 }} />
                                                                <span>Upload Combined Report</span>
                                                            </label>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Round Spectrometer Upload Card */}
                                            {(roundUploadReportMode === 'spectrometer' || (roundUploadReportMode === 'both' && roundBothUploadSubtype === 'separate')) && (
                                                <div className={`dual-upload-card ${spectrometerFile && !isCombinedFile ? 'has-file' : ''}`}>
                                                    <div className="upload-card-header">
                                                        <div className="upload-card-badge spec-badge">
                                                            <FileSpreadsheet size={15} />
                                                            <span>Spectrometer Output</span>
                                                        </div>
                                                        {isSpectrometerScanned && !isCombinedFile ? (
                                                            <span className="upload-status-chip">
                                                                <CheckCircle size={13} /> Scanned
                                                            </span>
                                                        ) : spectrometerFile && !isCombinedFile ? (
                                                            <span className="upload-status-chip" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #cbd5e1' }}>
                                                                <UploadCloud size={13} /> Uploaded
                                                            </span>
                                                        ) : null}
                                                    </div>

                                                    <div className="upload-card-body">
                                                        {spectrometerFile && !isCombinedFile ? (
                                                            <div className="upload-preview-wrap">
                                                                <div 
                                                                    className="upload-thumb" 
                                                                    onClick={() => openImageViewer([{ label: 'Spectrometer Output', url: spectrometerUrl, file: spectrometerFile }])} 
                                                                    title="Click to view full document"
                                                                >
                                                                    {isPdfFile(spectrometerFile, spectrometerUrl) ? (
                                                                        <div className="upload-thumb-pdf">
                                                                            <FileText size={22} className="pdf-icon" />
                                                                            <span className="upload-thumb-pdf-tag">PDF</span>
                                                                        </div>
                                                                    ) : (
                                                                        <img src={spectrometerUrl} alt="Spectrometer Output" />
                                                                    )}
                                                                    <div className="thumb-hover-overlay"><ZoomIn size={16} /></div>
                                                                </div>
                                                                <div className="upload-file-meta">
                                                                    <p className="file-name" title={spectrometerFile.name}>{spectrometerFile.name}</p>
                                                                    <p className="file-subtext">{isSpectrometerScanned ? 'Values extracted and reviewed' : 'Ready for scanning'}</p>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="upload-empty-state">
                                                                <UploadCloud size={26} className="upload-icon" />
                                                                <p className="upload-empty-title">Upload Spectrometer</p>
                                                                <p className="upload-empty-hint">Scan or report image</p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="upload-card-footer">
                                                        <input
                                                            type="file"
                                                            id="upload-round-spectrometer"
                                                            accept="image/*,application/pdf,.pdf"
                                                            style={{ display: 'none' }}
                                                            onChange={(e) => {
                                                                if (e.target.files[0]) {
                                                                    const file = e.target.files[0];
                                                                    setSpectrometerFile(file);
                                                                    setSpectrometerUrl(URL.createObjectURL(file));
                                                                    setIsSpectrometerScanned(false);
                                                                    e.target.value = null;
                                                                }
                                                            }}
                                                        />
                                                        {spectrometerFile && !isCombinedFile ? (
                                                            !hasScannedCurrentFiles && (
                                                                <div className="card-actions-row">
                                                                    <button
                                                                        type="button"
                                                                        className="btn-card-action btn-card-edit"
                                                                        onClick={(e) => { e.preventDefault(); openOcrModal(activeTransaction.itemSpecId, 'spectrometer', 'preview'); }}
                                                                    >
                                                                        Preview
                                                                    </button>
                                                                    <label
                                                                        htmlFor="upload-round-spectrometer"
                                                                        className="btn-card-action btn-card-change"
                                                                    >
                                                                        Change
                                                                    </label>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-card-action btn-card-remove"
                                                                        onClick={() => {
                                                                            if (spectrometerUrl) URL.revokeObjectURL(spectrometerUrl);
                                                                            setSpectrometerFile(null);
                                                                            setSpectrometerUrl(null);
                                                                        }}
                                                                        title="Remove Spectrometer Image"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            )
                                                        ) : (
                                                            <label
                                                                htmlFor={hasScannedCurrentFiles ? undefined : "upload-round-spectrometer"}
                                                                className={`btn-primary btn-upload-card ${hasScannedCurrentFiles ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                <UploadCloud size={15} style={{ flexShrink: 0 }} />
                                                                <span>Upload Spectrometer</span>
                                                            </label>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Round X-Ray Upload Card */}
                                            {(roundUploadReportMode === 'xray' || (roundUploadReportMode === 'both' && roundBothUploadSubtype === 'separate')) && (
                                                <div className={`dual-upload-card ${xrayFile && !isCombinedFile ? 'has-file' : ''}`}>
                                                    <div className="upload-card-header">
                                                        <div className="upload-card-badge xray-badge">
                                                            <Zap size={15} />
                                                            <span>X-Ray Output</span>
                                                        </div>
                                                        {isXrayScanned && !isCombinedFile ? (
                                                            <span className="upload-status-chip">
                                                                <CheckCircle size={13} /> Scanned
                                                            </span>
                                                        ) : xrayFile && !isCombinedFile ? (
                                                            <span className="upload-status-chip" style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #cbd5e1' }}>
                                                                <UploadCloud size={13} /> Uploaded
                                                            </span>
                                                        ) : null}
                                                    </div>

                                                    <div className="upload-card-body">
                                                        {xrayFile && !isCombinedFile ? (
                                                            <div className="upload-preview-wrap">
                                                                <div 
                                                                    className="upload-thumb" 
                                                                    onClick={() => openImageViewer([{ label: 'X-Ray Output', url: xrayUrl, file: xrayFile }])} 
                                                                    title="Click to view full document"
                                                                >
                                                                    {isPdfFile(xrayFile, xrayUrl) ? (
                                                                        <div className="upload-thumb-pdf">
                                                                            <FileText size={22} className="pdf-icon" />
                                                                            <span className="upload-thumb-pdf-tag">PDF</span>
                                                                        </div>
                                                                    ) : (
                                                                        <img src={xrayUrl} alt="X-Ray Output" />
                                                                    )}
                                                                    <div className="thumb-hover-overlay"><ZoomIn size={16} /></div>
                                                                </div>
                                                                <div className="upload-file-meta">
                                                                    <p className="file-name" title={xrayFile.name}>{xrayFile.name}</p>
                                                                    <p className="file-subtext">{isXrayScanned ? 'Values extracted and reviewed' : 'Ready for scanning'}</p>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="upload-empty-state">
                                                                <UploadCloud size={26} className="upload-icon" />
                                                                <p className="upload-empty-title">Upload X-Ray (XRF)</p>
                                                                <p className="upload-empty-hint">Scan or report image</p>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="upload-card-footer">
                                                        <input
                                                            type="file"
                                                            id="upload-round-xray"
                                                            accept="image/*,application/pdf,.pdf"
                                                            style={{ display: 'none' }}
                                                            onChange={(e) => {
                                                                if (e.target.files[0]) {
                                                                    const file = e.target.files[0];
                                                                    setXrayFile(file);
                                                                    setXrayUrl(URL.createObjectURL(file));
                                                                    setIsXrayScanned(false);
                                                                    e.target.value = null;
                                                                }
                                                            }}
                                                        />
                                                        {xrayFile && !isCombinedFile ? (
                                                            !hasScannedCurrentFiles && (
                                                                <div className="card-actions-row">
                                                                    <button
                                                                        type="button"
                                                                        className="btn-card-action btn-card-edit"
                                                                        onClick={(e) => { e.preventDefault(); openOcrModal(activeTransaction.itemSpecId, 'xray', 'preview'); }}
                                                                    >
                                                                        Preview
                                                                    </button>
                                                                    <label
                                                                        htmlFor="upload-round-xray"
                                                                        className="btn-card-action btn-card-change"
                                                                    >
                                                                        Change
                                                                    </label>
                                                                    <button
                                                                        type="button"
                                                                        className="btn-card-action btn-card-remove"
                                                                        onClick={() => {
                                                                            if (xrayUrl) URL.revokeObjectURL(xrayUrl);
                                                                            setXrayFile(null);
                                                                            setXrayUrl(null);
                                                                        }}
                                                                        title="Remove X-Ray Image"
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </div>
                                                            )
                                                        ) : (
                                                            <label
                                                                htmlFor={hasScannedCurrentFiles ? undefined : "upload-round-xray"}
                                                                className={`btn-primary btn-upload-card ${hasScannedCurrentFiles ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                <UploadCloud size={15} style={{ flexShrink: 0 }} />
                                                                <span>Upload X-Ray</span>
                                                            </label>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {(spectrometerFile || xrayFile) && (
                                            <div className="mt-4 mb-4 text-center">
                                                {hasScannedCurrentFiles ? (
                                                    <div className="flex items-center justify-center gap-4" style={{ padding: '10px 20px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', width: 'fit-content', margin: '0 auto' }}>
                                                        <span className="text-emerald-600 font-bold flex items-center text-sm">
                                                            <CheckCircle size={16} className="mr-2" /> Reports Scanned
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="btn-secondary flex items-center"
                                                            style={{ padding: '6px 12px', fontSize: '13px' }}
                                                            onClick={() => {
                                                                setIsSpectrometerScanned(false);
                                                                setIsXrayScanned(false);
                                                            }}
                                                        >
                                                            <Settings size={14} className="mr-1" /> Edit Uploads
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn-primary"
                                                        onClick={() => openOcrModal(activeTransaction.itemSpecId)}
                                                        style={{ padding: '10px 20px', fontSize: '14px', margin: '0 auto', display: 'inline-flex' }}
                                                    >
                                                        <Scan size={16} className="mr-2" /> Scan Uploaded Reports
                                                    </button>
                                                )}
                                            </div>
                                        )}

                                        {(spectrometerFile || xrayFile || Object.values(currentInputs).some(val => val !== '')) && (
                                            <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between items-center flex-wrap gap-2">
                                                <span className="text-xs text-slate-500 font-medium">
                                                    {spectrometerFile && xrayFile 
                                                        ? '✓ Both Spectrometer & X-Ray reports attached' 
                                                        : spectrometerFile 
                                                            ? '✓ Spectrometer report attached' 
                                                            : xrayFile 
                                                                ? '✓ X-Ray report attached' 
                                                                : 'Manual input values entered'}
                                                </span>
                                                <button
                                                    className={`btn-primary flex items-center gap-2 ${(isAnalyzing || hasPendingScans) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    style={{ padding: '8px 20px', fontSize: '13px', borderRadius: '8px' }}
                                                    onClick={processIteration}
                                                    disabled={isAnalyzing || hasPendingScans}
                                                >
                                                    {isAnalyzing ? <><RefreshCw className="spin mr-2" size={14} /> Calculating...</> : <><Settings className="mr-2" size={14} /> Calculate Round {activeTransaction.rounds.length + 1} Additives</>}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                            </motion.div>
                        ) : null}
                    </div>
                </div>
            </main >

            {/* OCR Multi-Step Modal */}
            <AnimatePresence>
                {ocrModal && (
                    <motion.div
                        key="ocr-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="modal-overlay"
                        style={{ zIndex: 1000 }}
                    >
                        <motion.div
                            initial={{ scale: 0.92, y: 24 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.92, y: 24 }}
                            style={{ background: '#fff', borderRadius: '16px', width: '90%', maxWidth: '680px', maxHeight: '90vh', overflowY: 'auto', padding: '32px', position: 'relative', boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }}
                        >
                            {/* Close */}
                            <button className="close-btn" onClick={closeOcrModal} style={{ position: 'absolute', top: 16, right: 16 }}>
                                <X size={22} />
                            </button>

                            {/* STEP 1: Preview */}
                            {ocrModal.step === 'preview' && (() => {
                                const activeReportType = ocrModal.activeTab;
                                const activeFile = activeReportType === 'spectrometer' ? ocrModal.spectrometerFile : ocrModal.xrayFile;
                                const activeUrl = activeReportType === 'spectrometer' ? ocrModal.spectrometerUrl : ocrModal.xrayUrl;
                                const activeIsPdf = isPdfFile(activeFile, activeUrl);

                                return (
                                <div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                        <div>
                                            <h3 className="text-primary m-0 mb-1" style={{ fontSize: '1.2rem' }}>
                                                Report Preview
                                            </h3>
                                            <p className="text-secondary text-sm mt-1 mb-0">
                                                Review the uploaded report before scanning.{activeIsPdf ? '' : ' Use +/− to zoom and drag to pan.'}
                                            </p>
                                        </div>
                                    </div>

                                    {ocrModal.spectrometerFile && ocrModal.xrayFile && (
                                        ocrModal.spectrometerFile === ocrModal.xrayFile ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                                                <span className="upload-card-badge combined-badge" style={{ padding: '6px 12px', fontSize: '12.5px' }}>
                                                    <Layers size={14} /> Combined Report (Spectrometer + X-Ray)
                                                </span>
                                                <span className="text-xs text-slate-500 font-medium">Both Spectrometer & X-Ray composition readings will be extracted from this document</span>
                                            </div>
                                        ) : (
                                            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                                                <button 
                                                    className={`btn-secondary ${ocrModal.activeTab === 'spectrometer' ? 'btn-primary' : ''}`}
                                                    style={{ padding: '6px 12px', fontSize: '14px', border: 'none', background: ocrModal.activeTab === 'spectrometer' ? '#4f46e5' : '#f1f5f9', color: ocrModal.activeTab === 'spectrometer' ? '#fff' : '#64748b', cursor: 'pointer', borderRadius: '6px' }}
                                                    onClick={() => setOcrModal(prev => ({ ...prev, activeTab: 'spectrometer', previewZoom: 1, panX: 0, panY: 0 }))}
                                                >
                                                    Spectrometer Report
                                                </button>
                                                <button 
                                                    className={`btn-secondary ${ocrModal.activeTab === 'xray' ? 'btn-primary' : ''}`}
                                                    style={{ padding: '6px 12px', fontSize: '14px', border: 'none', background: ocrModal.activeTab === 'xray' ? '#4f46e5' : '#f1f5f9', color: ocrModal.activeTab === 'xray' ? '#fff' : '#64748b', cursor: 'pointer', borderRadius: '6px' }}
                                                    onClick={() => setOcrModal(prev => ({ ...prev, activeTab: 'xray', previewZoom: 1, panX: 0, panY: 0 }))}
                                                >
                                                    X-Ray Report
                                                </button>
                                            </div>
                                        )
                                    )}

                                    {activeIsPdf ? (
                                        <div style={{ position: 'relative', width: '100%', height: '420px', background: '#f8fafc', borderRadius: '10px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                            <iframe
                                                src={activeUrl}
                                                title={`${ocrModal.activeTab === 'xray' ? 'X-Ray' : 'Spectrometer'} Report PDF`}
                                                style={{ width: '100%', height: '100%', border: 'none', background: '#ffffff', display: 'block' }}
                                            />
                                            <a
                                                href={activeUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                style={{
                                                    position: 'absolute',
                                                    top: 10,
                                                    right: 10,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    padding: '6px 12px',
                                                    fontSize: '12px',
                                                    background: 'rgba(255, 255, 255, 0.95)',
                                                    backdropFilter: 'blur(4px)',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                    borderRadius: '6px',
                                                    textDecoration: 'none',
                                                    color: '#1e293b',
                                                    fontWeight: 600,
                                                    zIndex: 10
                                                }}
                                            >
                                                Open in new tab <ExternalLink size={13} />
                                            </a>
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                position: 'relative',
                                                border: '1px solid #e2e8f0',
                                                borderRadius: '10px',
                                                overflow: 'hidden',
                                                height: '420px',
                                                background: '#f8fafc',
                                                cursor: (ocrModal.previewZoom || 1) > 1
                                                    ? (panRef.current.dragging ? 'grabbing' : 'grab')
                                                    : 'default',
                                                userSelect: 'none',
                                            }}
                                            onMouseDown={handlePanStart}
                                            onMouseMove={handlePanMove}
                                            onMouseUp={handlePanEnd}
                                            onMouseLeave={handlePanEnd}
                                        >
                                            {/* Inner layer that moves + scales */}
                                            <div
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transform: `translate(${ocrModal.panX || 0}px, ${ocrModal.panY || 0}px) scale(${ocrModal.previewZoom || 1})`,
                                                    transformOrigin: 'center',
                                                    transition: panRef.current.dragging ? 'none' : 'transform 0.15s ease',
                                                    willChange: 'transform',
                                                }}
                                            >
                                                <img
                                                    src={activeUrl}
                                                    alt="Lab report"
                                                    draggable={false}
                                                    style={{ maxWidth: '100%', maxHeight: '420px', objectFit: 'contain', display: 'block', pointerEvents: 'none' }}
                                                />
                                            </div>

                                            {/* Zoom controls overlay */}
                                            <div style={{ position: 'absolute', bottom: 10, right: 10, display: 'flex', flexDirection: 'column', gap: 4, zIndex: 10 }}>
                                                <button
                                                    className="btn-secondary"
                                                    style={{ padding: '6px 12px', fontSize: '16px', lineHeight: 1, background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', borderRadius: 6 }}
                                                    onClick={() => setOcrModal(prev => ({ ...prev, previewZoom: Math.min((prev.previewZoom || 1) + 0.5, 5) }))}
                                                >+</button>
                                                <button
                                                    className="btn-secondary"
                                                    style={{ padding: '6px 12px', fontSize: '16px', lineHeight: 1, background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', borderRadius: 6 }}
                                                    onClick={() => setOcrModal(prev => ({ ...prev, previewZoom: Math.max((prev.previewZoom || 1) - 0.5, 1), panX: 0, panY: 0 }))}
                                                >−</button>
                                            </div>

                                            {/* Zoom level badge */}
                                            <div style={{ position: 'absolute', bottom: 10, left: 12, background: 'rgba(0,0,0,0.45)', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '3px 8px', letterSpacing: '0.03em', zIndex: 10 }}>
                                                {Math.round((ocrModal.previewZoom || 1) * 100)}%
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex gap-3 mt-5 justify-end">
                                        <button className="btn-secondary" onClick={closeOcrModal}>Cancel</button>
                                        <button className="btn-primary flex items-center gap-2" onClick={runOcr}>
                                            Scan Values <ChevronRight size={16} />
                                        </button>
                                    </div>
                                </div>
                                );
                            })()}

                            {/* STEP 2: Scanning */}
                            {ocrModal.step === 'scanning' && (
                                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', marginBottom: 20 }}>
                                        <RefreshCw size={32} color="#fff" className="spin" />
                                    </div>
                                    <h3 className="text-primary m-0 mb-2">Scanning Reports</h3>
                                    <p className="text-secondary text-sm">Extracting element percentages...</p>
                                </div>
                            )}

                            {/* STEP 3: Review & Edit */}
                            {ocrModal.step === 'review' && (
                                <div onClick={() => setActiveOcrTooltip(null)}>
                                    <h3 className="text-primary m-0 mb-1" style={{ fontSize: '1.2rem' }}>
                                        Review Scanned Values
                                    </h3>
                                    {(() => {
                                        const bothReportsPresent = !!(ocrModal.spectrometerFile && ocrModal.xrayFile);
                                        return (
                                            <p className="text-secondary text-sm mt-1 mb-4">
                                                Values extracted from the reports are shown below. {bothReportsPresent ? 'You can click any Spectrometer or X-Ray value to switch the Final % Found, or edit entries directly before continuing.' : 'Edit any incorrect entries in the Final % Found column before continuing.'}
                                            </p>
                                        );
                                    })()}
                                    {(() => {
                                        const specId = ocrModal.targetSpecId;
                                        const spec = items.find(i => i.id === specId);
                                        const hasSpectroConsidered = (spec?.compounds || []).some(comp => !!getSpectrometerConsideredReason(comp.element));
                                        if (!hasSpectroConsidered) return null;
                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#4338ca', background: '#eef2ff', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #c7d2fe' }}>
                                                <Info size={16} color="#4f46e5" style={{ flexShrink: 0 }} />
                                                <span>
                                                    Highlighted rows indicate elements where <strong>Spectrometer</strong> is currently active over X-Ray (e.g. X-Ray &le; 0.09%). Hover or click the <strong>(i)</strong> icon for details.
                                                </span>
                                            </div>
                                        );
                                    })()}
                                    {(() => {
                                        const specId = ocrModal.targetSpecId;
                                        const spec = items.find(i => i.id === specId);
                                        const cuComp = (spec?.compounds || []).find(c => {
                                            const str = (c.element || '').trim().toUpperCase();
                                            return str.includes('COPPER') || str.includes('(CU)') || str === 'CU';
                                        });
                                        const isNoMaxCu = cuComp &&
                                            (!cuComp.spec_max || parseFloat(cuComp.spec_max) === 0) &&
                                            (!cuComp.target_max || parseFloat(cuComp.target_max) === 0);
                                        if (!isNoMaxCu) return null;

                                        const actualCu = parseFloat(ocrModal.scannedInputs?.[cuComp.element]) || 0;
                                        const sumOthers = (spec?.compounds || [])
                                            .filter(c => c.element !== cuComp.element)
                                            .reduce((sum, c) => sum + (parseFloat(ocrModal.scannedInputs?.[c.element]) || 0), 0);
                                        const computedCu = Math.max(0, parseFloat((100 - sumOthers).toFixed(4)));
                                        const cuMin = parseFloat(cuComp.target_min || cuComp.spec_min || 0);
                                        const diffAct = Math.abs(actualCu - cuMin);
                                        const diffComp = Math.abs(computedCu - cuMin);
                                        const nearestVal = diffComp < diffAct ? computedCu : actualCu;
                                        const nearestName = diffComp < diffAct ? 'Computed (100 − Σ others)' : 'Actual Lab Reading';

                                        return (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', fontSize: '13px', color: '#1e40af', background: '#eff6ff', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #bfdbfe', flexWrap: 'wrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Info size={16} color="#2563eb" style={{ flexShrink: 0 }} />
                                                    <span>
                                                        <strong>Copper Exception (No-Max Spec):</strong> Actual: <strong>{actualCu}%</strong> | Computed (100 − Σ others): <strong>{computedCu}%</strong> | Spec Min: <strong>{cuMin}%</strong>.
                                                    </span>
                                                </div>
                                                <span style={{ fontWeight: 600, color: '#1d4ed8', background: '#ffffff', padding: '2px 8px', borderRadius: '4px', border: '1px solid #dbeafe', whiteSpace: 'nowrap' }}>
                                                    Will use {nearestName}: {nearestVal}% (nearest to min)
                                                </span>
                                            </div>
                                        );
                                    })()}
                                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'visible', position: 'relative' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#f1f5f9' }}>
                                                    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', borderTopLeftRadius: '10px' }}>Element</th>
                                                    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Spec Range</th>
                                                    {ocrModal.spectrometerFile && (
                                                        <th style={{ padding: '10px 14px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#4f46e5', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>Spectrometer</th>
                                                    )}
                                                    {ocrModal.xrayFile && (
                                                        <th style={{ padding: '10px 14px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#eab308', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' }}>X-Ray</th>
                                                    )}
                                                    <th style={{ padding: '10px 14px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', borderTopRightRadius: '10px' }}>Final % Found</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const specId = ocrModal.targetSpecId;
                                                    const spec = items.find(i => i.id === specId);
                                                    const bothReportsPresent = !!(ocrModal.spectrometerFile && ocrModal.xrayFile);

                                                    return (spec?.compounds || []).map((comp, i) => {
                                                        const specReason = getSpectrometerConsideredReason(comp.element);
                                                        const isSpectroConsidered = !!specReason;

                                                        return (
                                                            <tr 
                                                                key={comp.element} 
                                                                style={{ 
                                                                    borderBottom: '1px solid #f1f5f9', 
                                                                    background: isSpectroConsidered ? '#eef2ff' : (i % 2 === 0 ? '#fff' : '#fafafa'),
                                                                    borderLeft: isSpectroConsidered ? '4px solid #4f46e5' : '4px solid transparent',
                                                                    transition: 'background 0.15s ease'
                                                                }}
                                                            >
                                                                <td style={{ padding: '10px 14px', fontWeight: 600, color: '#334155', fontSize: 14 }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                                                                        <span>{comp.element}</span>
                                                                        {isSpectroConsidered && (
                                                                            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                                                                                <button
                                                                                    type="button"
                                                                                    title={specReason}
                                                                                    style={{
                                                                                        width: '20px',
                                                                                        height: '20px',
                                                                                        borderRadius: '50%',
                                                                                        border: '1.5px solid #818cf8',
                                                                                        background: activeOcrTooltip === comp.element ? '#4f46e5' : '#e0e7ff',
                                                                                        color: activeOcrTooltip === comp.element ? '#ffffff' : '#4338ca',
                                                                                        cursor: 'pointer',
                                                                                        display: 'inline-flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        padding: 0,
                                                                                        transition: 'all 0.15s ease',
                                                                                        flexShrink: 0
                                                                                    }}
                                                                                    onMouseEnter={() => setActiveOcrTooltip(comp.element)}
                                                                                    onMouseLeave={() => setActiveOcrTooltip(null)}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setActiveOcrTooltip(prev => prev === comp.element ? null : comp.element);
                                                                                    }}
                                                                                >
                                                                                    <Info size={12} />
                                                                                </button>
                                                                                {activeOcrTooltip === comp.element && (
                                                                                    <div
                                                                                        style={{
                                                                                            position: 'absolute',
                                                                                            left: '26px',
                                                                                            top: '50%',
                                                                                            transform: 'translateY(-50%)',
                                                                                            zIndex: 999,
                                                                                            background: '#1e293b',
                                                                                            color: '#fff',
                                                                                            padding: '8px 12px',
                                                                                            borderRadius: '8px',
                                                                                            fontSize: '12px',
                                                                                            fontWeight: 500,
                                                                                            width: '260px',
                                                                                            boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
                                                                                            lineHeight: '1.4',
                                                                                            pointerEvents: 'none'
                                                                                        }}
                                                                                    >
                                                                                        <div style={{ fontWeight: 700, color: '#a5b4fc', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                                            <Info size={13} /> Spectrometer Output Selected
                                                                                        </div>
                                                                                        <div>{specReason}</div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: '#94a3b8' }}>
                                                                    {(() => {
                                                                        const minVal = comp.target_min || comp.spec_min || 0;
                                                                        const maxVal = comp.target_max || comp.spec_max;
                                                                        if (isCopperElement(comp.element) && (!maxVal || Number(maxVal) <= 0)) {
                                                                            return `≥ ${minVal}% (No Max)`;
                                                                        }
                                                                        return `${minVal}% – ${maxVal}%`;
                                                                    })()}
                                                                </td>
                                                                {ocrModal.spectrometerFile && (() => {
                                                                    const rawVal = ocrModal.scannedSpectrometerInputs?.[comp.element];
                                                                    const hasVal = rawVal !== '' && rawVal !== null && rawVal !== undefined && !isNaN(parseFloat(rawVal));

                                                                    if (!bothReportsPresent) {
                                                                        return (
                                                                            <td style={{ padding: '6px 14px', textAlign: 'center', fontSize: 13, fontFamily: 'monospace', color: '#4f46e5' }}>
                                                                                {hasVal ? rawVal : '-'}
                                                                            </td>
                                                                        );
                                                                    }

                                                                    const currentVal = ocrModal.scannedInputs[comp.element];
                                                                    const isSelected = hasVal && currentVal !== '' && currentVal !== null && currentVal !== undefined && parseFloat(currentVal) === parseFloat(rawVal);

                                                                    return (
                                                                        <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                                                                            {hasVal ? (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleRequestValueSwitch(comp.element, 'Spectrometer', rawVal);
                                                                                    }}
                                                                                    title={isSelected ? 'Spectrometer value is currently selected' : `Click to switch Final % to ${rawVal}% (Spectrometer)`}
                                                                                    style={{
                                                                                        background: isSelected ? '#e0e7ff' : '#f8fafc',
                                                                                        border: isSelected ? '1.5px solid #6366f1' : '1px dashed #c7d2fe',
                                                                                        borderRadius: '6px',
                                                                                        padding: '4px 8px',
                                                                                        cursor: isSelected ? 'default' : 'pointer',
                                                                                        fontFamily: 'monospace',
                                                                                        fontSize: '13px',
                                                                                        fontWeight: isSelected ? 700 : 600,
                                                                                        color: '#4338ca',
                                                                                        display: 'inline-flex',
                                                                                        flexDirection: 'column',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        transition: 'all 0.15s ease',
                                                                                        minWidth: '68px',
                                                                                        boxShadow: isSelected ? '0 1px 3px rgba(99,102,241,0.2)' : 'none'
                                                                                    }}
                                                                                    onMouseEnter={e => {
                                                                                        if (!isSelected) {
                                                                                            e.currentTarget.style.background = '#eef2ff';
                                                                                            e.currentTarget.style.borderColor = '#4f46e5';
                                                                                            e.currentTarget.style.transform = 'scale(1.04)';
                                                                                        }
                                                                                    }}
                                                                                    onMouseLeave={e => {
                                                                                        if (!isSelected) {
                                                                                            e.currentTarget.style.background = '#f8fafc';
                                                                                            e.currentTarget.style.borderColor = '#c7d2fe';
                                                                                            e.currentTarget.style.transform = 'scale(1)';
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <span>{rawVal}</span>
                                                                                    {isSelected ? (
                                                                                        <span style={{ fontSize: '9px', color: '#4f46e5', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>✓ Selected</span>
                                                                                    ) : (
                                                                                        <span style={{ fontSize: '9px', color: '#6366f1', opacity: 0.75 }}>click to use</span>
                                                                                    )}
                                                                                </button>
                                                                            ) : (
                                                                                <span style={{ fontSize: 13, fontFamily: 'monospace', color: '#94a3b8' }}>-</span>
                                                                            )}
                                                                        </td>
                                                                    );
                                                                })()}
                                                                {ocrModal.xrayFile && (() => {
                                                                    const rawVal = ocrModal.scannedXrayInputs?.[comp.element];
                                                                    const hasVal = rawVal !== '' && rawVal !== null && rawVal !== undefined && !isNaN(parseFloat(rawVal));

                                                                    if (!bothReportsPresent) {
                                                                        return (
                                                                            <td style={{ padding: '6px 14px', textAlign: 'center', fontSize: 13, fontFamily: 'monospace', color: '#eab308' }}>
                                                                                {hasVal ? rawVal : '-'}
                                                                            </td>
                                                                        );
                                                                    }

                                                                    const currentVal = ocrModal.scannedInputs[comp.element];
                                                                    const isSelected = hasVal && currentVal !== '' && currentVal !== null && currentVal !== undefined && parseFloat(currentVal) === parseFloat(rawVal);

                                                                    return (
                                                                        <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                                                                            {hasVal ? (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleRequestValueSwitch(comp.element, 'X-Ray', rawVal);
                                                                                    }}
                                                                                    title={isSelected ? 'X-Ray value is currently selected' : `Click to switch Final % to ${rawVal}% (X-Ray)`}
                                                                                    style={{
                                                                                        background: isSelected ? '#fef3c7' : '#f8fafc',
                                                                                        border: isSelected ? '1.5px solid #d97706' : '1px dashed #fde68a',
                                                                                        borderRadius: '6px',
                                                                                        padding: '4px 8px',
                                                                                        cursor: isSelected ? 'default' : 'pointer',
                                                                                        fontFamily: 'monospace',
                                                                                        fontSize: '13px',
                                                                                        fontWeight: isSelected ? 700 : 600,
                                                                                        color: '#b45309',
                                                                                        display: 'inline-flex',
                                                                                        flexDirection: 'column',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        transition: 'all 0.15s ease',
                                                                                        minWidth: '68px',
                                                                                        boxShadow: isSelected ? '0 1px 3px rgba(217,119,6,0.2)' : 'none'
                                                                                    }}
                                                                                    onMouseEnter={e => {
                                                                                        if (!isSelected) {
                                                                                            e.currentTarget.style.background = '#fffbeb';
                                                                                            e.currentTarget.style.borderColor = '#d97706';
                                                                                            e.currentTarget.style.transform = 'scale(1.04)';
                                                                                        }
                                                                                    }}
                                                                                    onMouseLeave={e => {
                                                                                        if (!isSelected) {
                                                                                            e.currentTarget.style.background = '#f8fafc';
                                                                                            e.currentTarget.style.borderColor = '#fde68a';
                                                                                            e.currentTarget.style.transform = 'scale(1)';
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <span>{rawVal}</span>
                                                                                    {isSelected ? (
                                                                                        <span style={{ fontSize: '9px', color: '#b45309', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>✓ Selected</span>
                                                                                    ) : (
                                                                                        <span style={{ fontSize: '9px', color: '#d97706', opacity: 0.75 }}>click to use</span>
                                                                                    )}
                                                                                </button>
                                                                            ) : (
                                                                                <span style={{ fontSize: 13, fontFamily: 'monospace', color: '#94a3b8' }}>-</span>
                                                                            )}
                                                                        </td>
                                                                    );
                                                                })()}
                                                                <td style={{ padding: '6px 14px', textAlign: 'center' }}>
                                                                    <input
                                                                        type="number"
                                                                        step="0.001"
                                                                        style={{ 
                                                                            width: '100%', 
                                                                            maxWidth: '90px', 
                                                                            padding: '7px 10px', 
                                                                            border: isSpectroConsidered ? '1.5px solid #818cf8' : '1.5px solid #c7d2fe', 
                                                                            borderRadius: 7, 
                                                                            fontFamily: 'monospace', 
                                                                            fontWeight: 700, 
                                                                            fontSize: 15, 
                                                                            color: '#1e293b', 
                                                                            textAlign: 'center', 
                                                                            outline: 'none', 
                                                                            background: isSpectroConsidered ? '#ffffff' : '#eef2ff', 
                                                                            margin: '0 auto', 
                                                                            display: 'block' 
                                                                        }}
                                                                        placeholder="0.000"
                                                                        value={ocrModal.scannedInputs[comp.element] ?? ''}
                                                                        onChange={(e) => handleOcrInputChange(comp.element, e.target.value)}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    });
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                    {(() => {
                                        const specId = ocrModal.targetSpecId;
                                        const spec = items.find(i => i.id === specId);
                                        const compounds = spec?.compounds || [];
                                        const zeroElements = compounds.filter(c => {
                                            const valStr = ocrModal.scannedInputs[c.element];
                                            if (valStr === '' || valStr === undefined || valStr === null) return true;
                                            const num = parseFloat(valStr);
                                            return isNaN(num) || num === 0;
                                        });
                                        const showWarning = zeroElements.length > 0 && !ocrModal.acknowledgedZeros;

                                        return (
                                            <>
                                                {showWarning && (
                                                    <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '16px', borderRadius: '12px', marginTop: '16px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                                            <div style={{ marginRight: '12px', marginTop: '2px', flexShrink: 0 }}>
                                                                <AlertTriangle color="#f59e0b" size={20} />
                                                            </div>
                                                            <div>
                                                                <h4 style={{ fontWeight: 'bold', margin: '0 0 4px 0', fontSize: '15px' }}>Warning: Zero Values Detected</h4>
                                                                <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>The following elements have a value of 0 or are missing: <strong>{zeroElements.map(c => c.element).join(', ')}</strong>.</p>
                                                                <button
                                                                    style={{ backgroundColor: '#fde68a', color: '#78350f', fontWeight: 'bold', padding: '6px 16px', borderRadius: '8px', fontSize: '14px', border: 'none', cursor: 'pointer', transition: 'background 0.2s' }}
                                                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#fcd34d'}
                                                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#fde68a'}
                                                                    onClick={() => setOcrModal(prev => ({ ...prev, acknowledgedZeros: true }))}
                                                                >
                                                                    I confirm these values are correct
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', gap: '12px', marginTop: '20px', alignItems: 'center' }}>
                                                    {(() => {
                                                        const reviewUrl = (ocrModal.activeTab === 'spectrometer' ? ocrModal.spectrometerUrl : ocrModal.xrayUrl) || ocrModal.previewUrl || ocrModal.spectrometerUrl || ocrModal.xrayUrl;
                                                        const reviewFile = (ocrModal.activeTab === 'spectrometer' ? ocrModal.spectrometerFile : ocrModal.xrayFile) || ocrModal.spectrometerFile || ocrModal.xrayFile;
                                                        const isPdf = isPdfFile(reviewFile, reviewUrl);
                                                        if (!reviewUrl) return null;
                                                        return (
                                                            <a
                                                                href={reviewUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                style={{ fontSize: '0.875rem', color: '#4f46e5', textDecoration: 'none', fontWeight: 600, marginRight: 'auto', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                                            >
                                                                {isPdf ? <FileText size={15} /> : <Image size={15} />}
                                                                {isPdf ? 'View Uploaded PDF' : 'View Uploaded Image'}
                                                                <ExternalLink size={13} />
                                                            </a>
                                                        );
                                                    })()}
                                                    <button className="btn-secondary" onClick={closeOcrModal}>Cancel</button>
                                                    {!showWarning && (
                                                        activeTransaction ? (
                                                            <div className="flex gap-2">
                                                                <button
                                                                    type="button"
                                                                    className="btn-secondary flex items-center gap-1.5"
                                                                    onClick={() => confirmOcrValues(false)}
                                                                >
                                                                    <CheckCircle size={14} /> Save Values
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`btn-primary flex items-center gap-2 ${isAnalyzing ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                                    onClick={() => confirmOcrValues(true)}
                                                                    disabled={isAnalyzing}
                                                                >
                                                                    {isAnalyzing ? <><RefreshCw size={14} className="spin" /> Processing...</> : <><Settings size={14} /> Calculate & Add Round</>}
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="btn-primary flex items-center gap-2"
                                                                onClick={() => confirmOcrValues(false)}
                                                            >
                                                                <CheckCircle size={14} /> Confirm Values
                                                            </button>
                                                        )
                                                    )}
                                                </div>
                                            </>
                                        );
                                    })()}

                                    {/* Value Switch Confirmation Alert Modal */}
                                    <AnimatePresence>
                                        {switchConfirmModal && (
                                            <motion.div
                                                key="switch-confirm-overlay"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                style={{
                                                    position: 'fixed',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    backgroundColor: 'rgba(15, 23, 42, 0.65)',
                                                    backdropFilter: 'blur(3px)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    zIndex: 1200,
                                                    padding: '20px'
                                                }}
                                                onClick={handleCancelValueSwitch}
                                            >
                                                <motion.div
                                                    initial={{ scale: 0.92, y: 16 }}
                                                    animate={{ scale: 1, y: 0 }}
                                                    exit={{ scale: 0.92, y: 16 }}
                                                    style={{
                                                        background: '#fff',
                                                        borderRadius: '14px',
                                                        width: '100%',
                                                        maxWidth: '440px',
                                                        padding: '24px',
                                                        boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
                                                        position: 'relative'
                                                    }}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '16px' }}>
                                                        <div style={{
                                                            width: '42px',
                                                            height: '42px',
                                                            borderRadius: '10px',
                                                            background: switchConfirmModal.source === 'Spectrometer' ? '#e0e7ff' : '#fef3c7',
                                                            color: switchConfirmModal.source === 'Spectrometer' ? '#4f46e5' : '#d97706',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0
                                                        }}>
                                                            <AlertCircle size={24} />
                                                        </div>
                                                        <div>
                                                            <h4 style={{ margin: '0 0 4px 0', fontSize: '17px', fontWeight: 700, color: '#1e293b' }}>
                                                                Confirm Value Switch
                                                            </h4>
                                                            <p style={{ margin: 0, fontSize: '14px', color: '#64748b', lineHeight: 1.4 }}>
                                                                Switch Final % for <strong>{switchConfirmModal.element}</strong> to the <strong>{switchConfirmModal.source}</strong> reading?
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div style={{
                                                        background: '#f8fafc',
                                                        borderRadius: '10px',
                                                        border: '1px solid #e2e8f0',
                                                        padding: '14px 16px',
                                                        marginBottom: '20px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-around'
                                                    }}>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>Current</div>
                                                            <div style={{ fontFamily: 'monospace', fontSize: '16px', fontWeight: 700, color: '#64748b' }}>
                                                                {switchConfirmModal.currentValue !== '' ? `${switchConfirmModal.currentValue}%` : '-'}
                                                            </div>
                                                        </div>
                                                        <div style={{ color: '#94a3b8', fontSize: '18px', fontWeight: 700 }}>→</div>
                                                        <div style={{ textAlign: 'center' }}>
                                                            <div style={{ fontSize: '11px', fontWeight: 600, color: switchConfirmModal.source === 'Spectrometer' ? '#4f46e5' : '#d97706', textTransform: 'uppercase', marginBottom: '2px' }}>
                                                                New ({switchConfirmModal.source})
                                                            </div>
                                                            <div style={{ fontFamily: 'monospace', fontSize: '17px', fontWeight: 700, color: switchConfirmModal.source === 'Spectrometer' ? '#4338ca' : '#b45309' }}>
                                                                {switchConfirmModal.value}%
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                                        <button
                                                            type="button"
                                                            className="btn-secondary"
                                                            style={{ padding: '8px 16px', fontSize: '14px' }}
                                                            onClick={handleCancelValueSwitch}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn-primary"
                                                            style={{
                                                                padding: '8px 18px',
                                                                fontSize: '14px',
                                                                background: switchConfirmModal.source === 'Spectrometer' ? '#4f46e5' : '#d97706',
                                                                borderColor: switchConfirmModal.source === 'Spectrometer' ? '#4338ca' : '#b45309'
                                                            }}
                                                            onClick={handleConfirmValueSwitch}
                                                        >
                                                            OK, Change Value
                                                        </button>
                                                    </div>
                                                </motion.div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}

                            {/* STEP 4: Analyzing */}
                            {ocrModal.step === 'analyzing' && (
                                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg,#f59e0b,#ef4444)', marginBottom: 20 }}>
                                        <RefreshCw size={32} color="#fff" className="spin" />
                                    </div>
                                    <h3 className="text-primary m-0 mb-2">{userModule === 'FE' ? 'Analyzing Additives' : 'Analyzing Interdependencies'}</h3>
                                    <p className="text-secondary text-sm">The algorithm is assessing chemical relationships and generating charge adjustments...</p>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Report Mismatch Alert Modal */}
            <AnimatePresence>
                {mismatchModal && (
                    <motion.div
                        key="report-mismatch-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="modal-overlay"
                        style={{ zIndex: 1300, backgroundColor: 'rgba(15, 23, 42, 0.72)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                        onClick={() => { setMismatchModal(null); closeOcrModal(); }}
                    >
                        <motion.div
                            initial={{ scale: 0.92, y: 16 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.92, y: 16 }}
                            style={{
                                background: '#fff',
                                borderRadius: '16px',
                                width: '100%',
                                maxWidth: '460px',
                                padding: '26px',
                                boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
                                position: 'relative'
                            }}
                            onClick={e => e.stopPropagation()}
                        >
                            <button 
                                type="button"
                                className="close-btn" 
                                onClick={() => { setMismatchModal(null); closeOcrModal(); }} 
                                style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                            >
                                <X size={20} />
                            </button>

                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '16px' }}>
                                <div style={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '12px',
                                    background: '#fef2f2',
                                    color: '#dc2626',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    border: '1px solid #fee2e2'
                                }}>
                                    <AlertTriangle size={24} />
                                </div>
                                <div>
                                    <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>
                                        Report Mismatch
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.4 }}>
                                        The uploaded <strong>{mismatchModal.reportType}</strong> report does not match the item selected in the dashboard.
                                    </p>
                                </div>
                            </div>

                            <div style={{
                                background: '#f8fafc',
                                borderRadius: '12px',
                                border: '1px solid #e2e8f0',
                                padding: '14px 16px',
                                marginBottom: '20px'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Selected Item Code
                                    </span>
                                    <span style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: 700, color: '#0f172a', background: '#e2e8f0', padding: '3px 8px', borderRadius: '6px' }}>
                                        {mismatchModal.expectedItem || 'Not Specified'}
                                    </span>
                                </div>
                                {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        Report Detected Code
                                    </span>
                                    <span style={{ fontFamily: 'monospace', fontSize: '14px', fontWeight: 700, color: '#dc2626', background: '#fee2e2', padding: '3px 8px', borderRadius: '6px' }}>
                                        {mismatchModal.detectedItem || 'Unknown / Missing'}
                                    </span>
                                </div> */}
                            </div>

                            <p style={{ margin: '0 0 20px 0', fontSize: '12px', color: '#94a3b8', lineHeight: 1.4 }}>
                                Please verify the report file and make sure you upload the document corresponding to the selected item.
                            </p>

                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    className="btn-primary"
                                    style={{
                                        padding: '9px 24px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        borderRadius: '8px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => {
                                        setMismatchModal(null);
                                        closeOcrModal();
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Close Batch Modal */}
            <AnimatePresence>
                {showCloseBatchModal && (
                    <motion.div
                        key="close-batch-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="modal-overlay"
                        style={{ zIndex: 1001 }}
                    >
                        <motion.div
                            initial={{ scale: 0.92, y: 24 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.92, y: 24 }}
                            style={{ background: '#fff', borderRadius: '16px', width: '90%', maxWidth: '480px', padding: '32px', position: 'relative', boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }}
                        >
                            <button className="close-btn" onClick={() => { setShowCloseBatchModal(false); setClosureRemarks(''); }} style={{ position: 'absolute', top: 16, right: 16 }}>
                                <X size={22} />
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fff0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <AlertTriangle size={20} color="#ef4444" />
                                </div>
                                <h3 className="text-primary m-0" style={{ fontSize: '1.15rem' }}>Close Batch Manually</h3>
                            </div>
                            <p className="text-secondary text-sm mb-4">This will mark the batch as <strong>Manually Closed</strong>. No further rounds can be added. Please provide a reason for closure.</p>
                            <div style={{ marginBottom: 20 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>Closure Remarks <span style={{ color: '#94a3b8', fontWeight: 400 }}>(required)</span></label>
                                <textarea
                                    rows={3}
                                    className="input-field"
                                    style={{ resize: 'vertical', minHeight: 80 }}
                                    placeholder="e.g. Material constraints, customer requirement change, time limit reached..."
                                    value={closureRemarks}
                                    onChange={(e) => setClosureRemarks(e.target.value)}
                                />
                            </div>
                            <div className="flex gap-3 justify-end">
                                <button className="btn-secondary" onClick={() => { setShowCloseBatchModal(false); setClosureRemarks(''); }}>Cancel</button>
                                <button
                                    className={`btn-primary flex items-center gap-2 ${(!closureRemarks.trim() || isClosing) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)' }}
                                    onClick={handleCloseBatch}
                                    disabled={!closureRemarks.trim() || isClosing}
                                >
                                    {isClosing ? <><RefreshCw size={14} className="spin" /> Closing...</> : <><X size={14} /> Confirm Close</>}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Empty Items Modal */}
            <AnimatePresence>
                {showEmptyItemsModal && isQualityManager && (
                    <motion.div
                        key="empty-items-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="modal-overlay"
                        style={{ zIndex: 1002 }}
                    >
                        <motion.div
                            initial={{ scale: 0.92, y: 24 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.92, y: 24 }}
                            style={{ background: '#fff', borderRadius: '16px', width: '90%', maxWidth: '400px', padding: '32px', position: 'relative', boxShadow: '0 24px 60px rgba(0,0,0,0.18)', textAlign: 'center' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                                <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <AlertTriangle size={32} color="#f59e0b" />
                                </div>
                            </div>
                            <h3 className="text-primary m-0 mb-2" style={{ fontSize: '1.25rem' }}>No Items Found</h3>
                            <p className="text-secondary text-sm mb-6">No Items available. Please setup the Master data</p>
                            <button
                                className="btn-primary w-full flex items-center justify-center py-3 gap-2"
                                onClick={() => { setShowEmptyItemsModal(false); setShowSecretModal(true); }}
                            >
                                <PlusCircle size={18} /> Add
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showSecretModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="modal-overlay"
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            className="secret-modal glass-panel"
                        >
                            <button
                                className="close-btn"
                                onClick={() => {
                                    setShowSecretModal(false);
                                    setSecretCode('');
                                    setError('');
                                }}
                            >
                                <X size={24} />
                            </button>

                            <div className="secret-modal-header">
                                <div className="icon-container-small">
                                    <KeyRound size={32} color="#10b981" />
                                </div>
                                <h3 className="text-primary">Master Database</h3>
                                <p>Enter master code to override spec limits.</p>
                            </div>

                            <form onSubmit={handleAccessItems}>
                                <div className="input-group mb-4">
                                    <KeyRound className="input-icon" size={20} />
                                    <input
                                        type="password"
                                        className="input-field with-icon pl-10"
                                        placeholder="Secure Code"
                                        value={secretCode}
                                        onChange={(e) => setSecretCode(e.target.value)}
                                        autoFocus
                                    />
                                </div>

                                {error && <p className="error-message mb-4">{error}</p>}

                                <button type="submit" className="btn-primary w-full flex justify-center py-3">
                                    Authorize Access
                                </button>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Charge Addition Summary Modal ──────────────────────────── */}
            <AnimatePresence>
                {chargeModal && (() => {
                    // ── Normalized aggregation over round.additions ───────────────────────
                    // Rules:
                    //   Add / LossComp  → use add.lbs directly. The engine already stores the
                    //                     physical charge weight (M_physical = M_effective / recovery_factor).
                    //                     Multiplying again by (1 + lossFactor/100) would double-compensate.
                    //   Swap            → per-step lbs are meaningless when the engine oscillates.
                    //                     Use net mass delta from snapshots instead.
                    //   Cut             → material is removed, nothing is added. Skip entirely.

                    const agg = {}; // key: element code → { element, totalLbs }
                    const additions = chargeModal.round.additions || [];
                    const snapshots = chargeModal.round.snapshots || [];

                    // ── Pass 1: Add + LossComp — use add.lbs directly ────────────────────
                    // The engine already computes the physical charge weight:
                    //   M_physical = M_effective / recovery_factor
                    // so add.lbs IS the correct lbs to put into the furnace. No extra multiply.
                    additions.forEach(add => {
                        if (add.action !== 'Add' && add.action !== 'LossComp') return; // Skip Swap & Cut
                        const key = add.element;
                        if (!agg[key]) agg[key] = { element: key, totalLbs: 0 };
                        agg[key].totalLbs += add.lbs;
                    });

                    // ── Pass 2: Swap — use net mass delta from snapshots ──────────────────
                    // Per-step swap lbs are useless when the engine oscillates (they inflate
                    // enormously). The only meaningful figure is: finalMass - priorToSwapMass.
                    const hasSwaps = additions.some(a => a.action === 'Swap');
                    if (hasSwaps && snapshots.length >= 2) {
                        const firstSwapStep = additions.find(a => a.action === 'Swap')?.step ?? Infinity;
                        // Find the last snapshot that was captured BEFORE the first Swap
                        const preSwapSnap = [...snapshots]
                            .filter(s => s.step < firstSwapStep)
                            .sort((a, b) => b.step - a.step)[0];
                        const finalSnap = snapshots[snapshots.length - 1];

                        if (preSwapSnap && finalSnap && preSwapSnap !== finalSnap) {
                            Object.keys(finalSnap.elements).forEach(code => {
                                const preMass = preSwapSnap.elements[code]?.mass ?? 0;
                                const finalMass = finalSnap.elements[code]?.mass ?? 0;
                                const netDelta = parseFloat((finalMass - preMass).toFixed(2));
                                if (netDelta > 0.01) { // positive delta only — net addition
                                    if (!agg[code]) agg[code] = { element: code, totalLbs: 0 };
                                    agg[code].totalLbs += netDelta;
                                }
                            });
                        }
                    }
                    // (Cut actions are skipped entirely — nothing is added to the charge)

                    // ── Final rows: round totals, sort descending, apply search ───────────
                    const rows = Object.values(agg)
                        .map(r => ({ element: r.element, lbs: parseFloat(r.totalLbs.toFixed(2)) }))
                        .filter(r => r.lbs > 0)
                        .sort((a, b) => b.lbs - a.lbs)
                        .filter(r => r.element.toLowerCase().includes(chargeModalSearch.toLowerCase()));

                    const totalLbs = rows.reduce((s, r) => s + r.lbs, 0);

                    return (
                        <motion.div
                            key="charge-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <motion.div
                                initial={{ scale: 0.92, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.92, y: 20 }}
                                onClick={e => e.stopPropagation()}
                                style={{ background: '#fff', borderRadius: '20px', width: '92%', maxWidth: '460px', boxShadow: '0 32px 80px rgba(15,23,42,0.22)', overflow: 'hidden' }}
                            >
                                {/* Header */}
                                <div style={{ background: userModule === 'FE' ? 'linear-gradient(135deg, #475569 0%, #ea580c 100%)' : 'linear-gradient(135deg, #0284c7 0%, #4f46e5 100%)', padding: '22px 24px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div>
                                        <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Round {chargeModal.round.roundNumber}</p>
                                        <h3 style={{ margin: '2px 0 0', fontSize: '18px', fontWeight: 800, color: '#fff' }}>Addition Summary</h3>
                                    </div>
                                    <button onClick={() => { setChargeModal(null); setChargeModalSearch(''); }} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', padding: '6px 8px', cursor: 'pointer', color: '#fff', display: 'flex' }}>
                                        <X size={18} />
                                    </button>
                                </div>

                                {/* Search */}
                                <div style={{ padding: '14px 24px 0' }}>
                                    <input
                                        autoFocus
                                        value={chargeModalSearch}
                                        onChange={e => setChargeModalSearch(e.target.value)}
                                        placeholder="Search element..."
                                        style={{ width: '100%', padding: '9px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '13px', outline: 'none', boxSizing: 'border-box', background: '#f8fafc', color: '#1e293b' }}
                                    />
                                </div>

                                {/* Rows */}
                                <div style={{ padding: '12px 24px 6px', maxHeight: '340px', overflowY: 'auto' }}>
                                    {rows.length === 0 ? (
                                        <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px', padding: '20px 0' }}>No elements found.</p>
                                    ) : rows.map((row, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderRadius: '10px', marginBottom: '6px', background: i % 2 === 0 ? '#f8fafc' : '#fff', border: '1px solid #f1f5f9' }}>
                                            <span style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>{row.element}</span>
                                            <span style={{ fontSize: '15px', fontWeight: 800, color: userModule === 'FE' ? '#ea580c' : '#0284c7', fontFamily: 'monospace' }}>{row.lbs?.toLocaleString()} <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8' }}>lbs</span></span>
                                        </div>
                                    ))}
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', borderRadius: '10px', marginBottom: '6px', background: '#fff', border: '1px solid #f1f5f9' }}>
                                        <span style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>P Cu</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="text"
                                                value={pCuInput}
                                                onChange={(e) => setPCuInput(e.target.value)}
                                                placeholder="Enter P Cu"
                                                style={{ width: '120px', padding: '6px 10px', borderRadius: '6px', border: pCuError ? '1.5px solid #ef4444' : '1.5px solid #e2e8f0', fontSize: '13px', outline: 'none', background: '#f8fafc', color: '#1e293b' }}
                                            />
                                            <button
                                                onClick={handleSavePCu}
                                                disabled={pCuSaving}
                                                style={{ padding: '6px 12px', borderRadius: '6px', background: userModule === 'FE' ? 'linear-gradient(45deg, #475569, #ea580c)' : 'linear-gradient(45deg, #0284c7, #4f46e5)', color: '#fff', border: 'none', cursor: pCuSaving ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold', opacity: pCuSaving ? 0.7 : 1 }}
                                            >
                                                {pCuSaving ? 'Saving…' : (chargeModal.round.pCu && pCuInput === chargeModal.round.pCu) ? 'Edit' : 'Save'}
                                            </button>
                                        </div>
                                    </div>
                                    {pCuError && <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', textAlign: 'right' }}>{pCuError}</p>}
                                </div>

                                {/* Footer total */}
                                <div style={{ padding: '12px 24px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>TOTAL CHARGE</span>
                                    <span
                                        style={{
                                            fontSize: '16px',
                                            fontWeight: 800,
                                            color: '#1e293b',
                                            fontFamily: 'monospace'
                                        }}
                                    >
                                        {`${(
                                            parseFloat(totalLbs || 0) + parseFloat(pCuInput || 0)
                                        )?.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })} lbs`}
                                    </span>{' '}
                                </div>
                            </motion.div>
                        </motion.div>
                    );
                })()}
            </AnimatePresence>

            {/* ── Snapshot Detail Modal ────────────────────────────────────── */}
            <AnimatePresence>
                {snapshotModal && (() => {
                    const { snapshot, roundNumber, allSnapshots } = snapshotModal;
                    const elementCodes = Object.keys(snapshot.elements);
                    const currentIdx = allSnapshots.findIndex(s => s.step === snapshot.step && s.action === snapshot.action);
                    const prevSnap = currentIdx > 0 ? allSnapshots[currentIdx - 1] : null;
                    return (
                        <motion.div
                            key="snapshot-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => setSnapshotModal(null)}
                        >
                            <motion.div
                                initial={{ scale: 0.92, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.92, y: 20 }}
                                onClick={e => e.stopPropagation()}
                                style={{ background: '#fff', borderRadius: '20px', width: '94%', maxWidth: '620px', boxShadow: '0 32px 80px rgba(15,23,42,0.22)', overflow: 'hidden' }}
                            >
                                {/* Header */}
                                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)', padding: '20px 24px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div>
                                        <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Round {roundNumber} — Step Breakdown</p>
                                        <h3 style={{ margin: '2px 0 0', fontSize: '17px', fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {snapshot.action === 'Initial' ? 'Initial Melt State' : (
                                                <>
                                                    After Step {snapshot.step}:
                                                    <span style={{
                                                        fontSize: '12px', fontWeight: 700, padding: '2px 10px', borderRadius: '20px',
                                                        background: snapshot.action === 'Add' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
                                                        color: snapshot.action === 'Add' ? '#6ee7b7' : '#fca5a5',
                                                    }}>
                                                        {snapshot.action === 'Add' ? `▲ Add ${snapshot.element}` : `▼ Cut ${snapshot.element}`}
                                                    </span>
                                                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace' }}>{snapshot.lbs} lbs</span>
                                                </>
                                            )}
                                        </h3>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        {/* Step navigation */}
                                        <button
                                            disabled={currentIdx <= 0}
                                            onClick={() => { if (currentIdx > 0) setSnapshotModal(prev => ({ ...prev, snapshot: allSnapshots[currentIdx - 1] })); }}
                                            style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '6px', padding: '5px 10px', cursor: currentIdx > 0 ? 'pointer' : 'not-allowed', color: currentIdx > 0 ? '#fff' : 'rgba(255,255,255,0.3)', fontSize: '13px', fontWeight: 700 }}
                                        >← Prev</button>
                                        <button
                                            disabled={currentIdx >= allSnapshots.length - 1}
                                            onClick={() => { if (currentIdx < allSnapshots.length - 1) setSnapshotModal(prev => ({ ...prev, snapshot: allSnapshots[currentIdx + 1] })); }}
                                            style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '6px', padding: '5px 10px', cursor: currentIdx < allSnapshots.length - 1 ? 'pointer' : 'not-allowed', color: currentIdx < allSnapshots.length - 1 ? '#fff' : 'rgba(255,255,255,0.3)', fontSize: '13px', fontWeight: 700 }}
                                        >Next →</button>
                                        <button onClick={() => setSnapshotModal(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', padding: '6px 8px', cursor: 'pointer', color: '#fff', display: 'flex', marginLeft: '4px' }}>
                                            <X size={18} />
                                        </button>
                                    </div>
                                </div>

                                {/* Weight summary bar */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Scale size={15} style={{ color: '#6366f1' }} />
                                        <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Charge Weight</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '18px', fontWeight: 800, color: '#1e293b', fontFamily: 'monospace' }}>{snapshot.totalWeight?.toLocaleString()} lbs</span>
                                        {prevSnap && (
                                            <span style={{ fontSize: '11px', fontWeight: 700, color: snapshot.totalWeight > prevSnap.totalWeight ? '#059669' : '#dc2626', background: snapshot.totalWeight > prevSnap.totalWeight ? '#ecfdf5' : '#fef2f2', padding: '2px 8px', borderRadius: '12px' }}>
                                                {snapshot.totalWeight > prevSnap.totalWeight ? '+' : ''}{(snapshot.totalWeight - prevSnap.totalWeight).toFixed(2)}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Element table */}
                                <div style={{ padding: '0 24px', maxHeight: '360px', overflowY: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                                <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Element</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Mass (lbs)</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Percentage</th>
                                                <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Δ Change</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {elementCodes.map((code, i) => {
                                                const el = snapshot.elements[code];
                                                const prevEl = prevSnap?.elements?.[code];
                                                const pctDelta = prevEl ? (el.pct - prevEl.pct) : null;
                                                const isChanged = pctDelta !== null && Math.abs(pctDelta) > 0.0001;
                                                return (
                                                    <tr key={code} style={{ borderBottom: '1px solid #f1f5f9', background: isChanged ? (pctDelta > 0 ? 'rgba(16,185,129,0.04)' : 'rgba(239,68,68,0.04)') : (i % 2 === 0 ? '#fff' : '#fafbfc') }}>
                                                        <td style={{ padding: '10px 8px', fontWeight: 700, fontSize: '13px', color: '#1e293b' }}>{code}</td>
                                                        <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '13px', fontWeight: 600, color: '#475569' }}>{el.mass?.toLocaleString()}</td>
                                                        <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '13px', fontWeight: 700, color: '#1e293b' }}>{el.pct.toFixed(4)}%</td>
                                                        <td style={{ padding: '10px 8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '12px', fontWeight: 700 }}>
                                                            {pctDelta === null ? (
                                                                <span style={{ color: '#cbd5e1' }}>—</span>
                                                            ) : Math.abs(pctDelta) < 0.0001 ? (
                                                                <span style={{ color: '#94a3b8' }}>0</span>
                                                            ) : (
                                                                <span style={{ color: pctDelta > 0 ? '#059669' : '#dc2626' }}>
                                                                    {pctDelta > 0 ? '+' : ''}{pctDelta.toFixed(4)}%
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Footer — step indicator */}
                                <div style={{ padding: '14px 24px 18px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    {allSnapshots.map((s, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setSnapshotModal(prev => ({ ...prev, snapshot: s }))}
                                            style={{
                                                width: '28px', height: '28px', borderRadius: '50%',
                                                border: s.step === snapshot.step && s.action === snapshot.action ? '2px solid #4f46e5' : '1.5px solid #e2e8f0',
                                                background: s.step === snapshot.step && s.action === snapshot.action ? '#4f46e5' : '#fff',
                                                color: s.step === snapshot.step && s.action === snapshot.action ? '#fff' : '#64748b',
                                                cursor: 'pointer', fontSize: '11px', fontWeight: 800,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                transition: 'all 0.15s ease',
                                            }}
                                            title={s.action === 'Initial' ? 'Initial State' : `Step ${s.step}: ${s.action} ${s.element}`}
                                        >
                                            {s.step === 0 ? '⊙' : s.step}
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        </motion.div>
                    );
                })()}
            </AnimatePresence>

            {/* Expired Melt Confirmation Modal */}
            <AnimatePresence>
                {expiredModalBatchId && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="pour-modal-overlay"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="pour-modal-card"
                        >
                            <div className="pour-modal-icon">
                                <AlertTriangle size={28} />
                            </div>
                            <h3 className="pour-modal-title">Pour Window Expired</h3>
                            <p className="pour-modal-msg">
                                Batch {expiredModalBatchId} is marked as Expired.
                            </p>
                            <button
                                onClick={() => setExpiredModalBatchId(null)}
                                className="btn-acknowledge-close"
                            >
                                Acknowledge & Close
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* View Lab Report / Image Modal */}
            <AnimatePresence>
                {viewImageModal && (() => {
                    const activeImgObj = viewImageModal.images[viewImageModal.activeIndex];
                    const activeIsPdf = isPdfFile(activeImgObj?.file, activeImgObj?.url);

                    return (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        style={{
                            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.85)', backdropFilter: 'blur(6px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}
                        onClick={() => { setViewImageModal(null); setZoomLevel(1); }}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: '#1e293b', padding: '20px', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', width: '95vw', height: '95vh', maxWidth: '1600px', display: 'flex', flexDirection: 'column'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                    <h3 style={{ margin: 0, color: 'white', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {activeIsPdf ? <FileText size={20} /> : <Image size={20} />} {activeImgObj?.label || 'Lab Report'}
                                    </h3>
                                    {viewImageModal.images.length > 1 && (
                                        <div className="viewer-tab-group">
                                            {viewImageModal.images.map((item, idx) => (
                                                <button
                                                    key={idx}
                                                    type="button"
                                                    className={`viewer-tab-btn ${viewImageModal.activeIndex === idx ? 'active' : ''}`}
                                                    onClick={() => {
                                                        setViewImageModal(prev => ({ ...prev, activeIndex: idx }));
                                                        setZoomLevel(1);
                                                    }}
                                                >
                                                    {item.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    {activeIsPdf ? (
                                        <a
                                            href={activeImgObj?.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                background: 'rgba(255,255,255,0.12)',
                                                color: '#ffffff',
                                                padding: '6px 12px',
                                                borderRadius: '8px',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                textDecoration: 'none'
                                            }}
                                            title="Open PDF in new tab"
                                        >
                                            <ExternalLink size={15} /> Open in new tab
                                        </a>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '4px' }}>
                                            <button onClick={() => setZoomLevel(prev => Math.max(0.5, prev - 0.25))} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: '4px' }}>
                                                <ZoomOut size={18} />
                                            </button>
                                            <span style={{ color: 'white', fontSize: '14px', width: '48px', textAlign: 'center' }}>
                                                {Math.round(zoomLevel * 100)}%
                                            </span>
                                            <button onClick={() => setZoomLevel(prev => Math.min(3, prev + 0.25))} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: '4px' }}>
                                                <ZoomIn size={18} />
                                            </button>
                                            <button onClick={() => setZoomLevel(1)} style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: '4px 8px', fontSize: '12px', borderLeft: '1px solid rgba(255,255,255,0.2)', marginLeft: '4px' }}>
                                                Reset
                                            </button>
                                        </div>
                                    )}
                                    <button
                                        onClick={() => { setViewImageModal(null); setZoomLevel(1); }}
                                        style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', padding: '6px', color: 'white', cursor: 'pointer', display: 'flex' }}
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                            </div>
                            {activeIsPdf ? (
                                <div style={{ flex: 1, height: '100%', width: '100%', borderRadius: '8px', overflow: 'hidden', background: '#0f172a' }}>
                                    <iframe
                                        src={activeImgObj?.url}
                                        title={activeImgObj?.label || 'Lab Report PDF'}
                                        style={{ width: '100%', height: '100%', border: 'none', background: '#ffffff', display: 'block' }}
                                    />
                                </div>
                            ) : (
                                <div 
                                    ref={imageContainerRef}
                                    onMouseDown={(e) => {
                                        if (zoomLevel <= 1) return;
                                        const container = imageContainerRef.current;
                                        if (!container) return;
                                        container.dataset.isDragging = 'true';
                                        container.dataset.startX = e.clientX;
                                        container.dataset.startY = e.clientY;
                                        container.dataset.scrollLeft = container.scrollLeft;
                                        container.dataset.scrollTop = container.scrollTop;
                                        container.style.cursor = 'grabbing';
                                    }}
                                    onMouseMove={(e) => {
                                        const container = imageContainerRef.current;
                                        if (container && container.dataset.isDragging === 'true') {
                                            const dx = e.clientX - parseFloat(container.dataset.startX);
                                            const dy = e.clientY - parseFloat(container.dataset.startY);
                                            container.scrollLeft = parseFloat(container.dataset.scrollLeft) - dx;
                                            container.scrollTop = parseFloat(container.dataset.scrollTop) - dy;
                                        }
                                    }}
                                    onMouseUp={() => {
                                        const container = imageContainerRef.current;
                                        if (container) {
                                            container.dataset.isDragging = 'false';
                                            container.style.cursor = zoomLevel > 1 ? 'grab' : 'default';
                                        }
                                    }}
                                    onMouseLeave={() => {
                                        const container = imageContainerRef.current;
                                        if (container) {
                                            container.dataset.isDragging = 'false';
                                            container.style.cursor = zoomLevel > 1 ? 'grab' : 'default';
                                        }
                                    }}
                                    style={{ flex: 1, overflow: 'auto', borderRadius: '8px', background: '#000', cursor: zoomLevel > 1 ? 'grab' : 'default' }}
                                >
                                    <div style={{
                                        width: `${zoomLevel * 100}%`,
                                        height: `${zoomLevel * 100}%`,
                                        minWidth: '100%',
                                        minHeight: '100%',
                                        display: 'flex',
                                        alignItems: zoomLevel > 1 ? 'flex-start' : 'center',
                                        justifyContent: zoomLevel > 1 ? 'flex-start' : 'center',
                                        transition: 'width 0.2s ease-in-out, height 0.2s ease-in-out'
                                    }}>
                                        <img 
                                            src={activeImgObj?.url} 
                                            alt={activeImgObj?.label || 'Lab Report'} 
                                            draggable={false} 
                                            style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} 
                                        />
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                    );
                })()}
            </AnimatePresence>
        </div>
    );
};

export default Dashboard;
